import React, { useEffect, useRef, useState } from "react";
import Chart from "chart.js/auto";
import jsPDF from "jspdf";
import "jspdf-autotable";
import styled from 'styled-components';

import axios from "axios";
import {
  Table,
  TableContainer,
  TableHead,
  TableBody,
  TableRow,
  TableCell,
  TablePagination,
  Paper,
  Select,
  MenuItem,
  Button,
  Checkbox,
} from "@mui/material";
import { toast } from "react-toastify";
import { useDispatch, useSelector } from "react-redux";
import { getStates } from "../features/statecity/statecitySlice";
import { getUsers } from "../features/cutomers/customerSlice";
import { base_url } from "../utils/baseUrl";
import { config } from "../utils/axiosconfig";
import {
  getSalary,
  csvDownload,
  createMonthSalary,
  uploadCsv,
  updatePaidSalary,
  deleteSalaryRecord,
} from "../features/salary-record/salarySlice";
import CsvValidationModal from "../components/CsvValidationModal";

/**
 * IMPORTANT: Rollback done on 19 June 2026
 * Removed: Edit Modal Popup feature (was added on 16 June 2026 in commit c3c9160)
 * Reason: Feature was incomplete, not properly tested, and causing issues
 * Rolled back to: 10 June 2026 state (commit 3194d42)
 * Note: Invoice-like edit popup removed - system restored to inline editing only
 */

const SalaryPayment = () => {
  const [isMobile, setIsMobile] = useState(window.innerWidth < 768);

  useEffect(() => {
    const handleResize = () => setIsMobile(window.innerWidth < 768);
    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  const dispatch = useDispatch();
  const { salaries, isLoading, error, csvData } = useSelector(
    (state) => state.salary
  );
  const { statelist } = useSelector((state) => state.statecity);
  const { customers: vclist } = useSelector((state) => state.customer);
  const { user } = useSelector((state) => state.auth);

  // Helper functions for access control
  const hasAdminPrivileges = () => {
    return user?.role === "superadmin" || user?.role === "hr";
  };

  const canEditSalary = () => {
    return user?.role === "superadmin" || 
           user?.role === "hr" || 
           user?.email === "accounts.entry@lnjskills.com";
  };

  const canDeleteSalary = () => {
    return user?.role === "superadmin";
  };

  const StyledCard = styled.div`
  background-color: ${props => props.color || "#3b82f6"};
  color: #ffffff;
  padding: 20px 16px;
  border-radius: 12px;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
  text-align: center;
  transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
  cursor: pointer;
  position: relative;
  overflow: hidden;
  min-height: 100px;
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;
  border: 1px solid rgba(255, 255, 255, 0.1);
  
  &:hover {
    transform: translateY(-4px) scale(1.02);
    box-shadow: 0 8px 25px rgba(0, 0, 0, 0.2);
    border-color: rgba(255, 255, 255, 0.3);
  }
  
  &::before {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    background: linear-gradient(135deg, rgba(255,255,255,0.1) 0%, transparent 50%);
    pointer-events: none;
  }
`;
  const chartRef = useRef(null);
  const chartInstanceRef = useRef(null);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [formData, setFormData] = useState({
    month: "",
    year: "",
    state: "",
    district: "",
    vc: "",
    salaryDue: 0,
    additionalDueAmount: 0,
  });
  const [filters, setFilters] = useState({
    month: "",
    year: "",
    state: "",
    district: "",
    vc: "",
    vt: "",
    fromDate: "",
    toDate: "",
  });
  const [filteredSalaries, setFilteredSalaries] = useState([]);
  const [invoiceSearch, setInvoiceSearch] = useState("");
  const [invoiceSearchInput, setInvoiceSearchInput] = useState(""); // Temporary input value
  
  // All state declarations together to avoid hoisting issues
  const [filteredSummary, setFilteredSummary] = useState({
    totalSalaryDue: 0,
    totalPaidSalary: 0,
    totalAdjustments: 0,
    totalUnpaidSalary: 0,
    totalDeductions: 0,
  });
  const [gapMetricsLoading, setGapMetricsLoading] = useState(true);
  const [gapMetrics, setGapMetrics] = useState({
    avgSalaryDueToPaid: 0,
    avgSalaryPaidToBillGen: 0,
    avgBillGenToSubmission: 0,
    totalCycleTime: 0,
  });
  const [refreshTrigger, setRefreshTrigger] = useState(0);
  const [selectedSalaryIds, setSelectedSalaryIds] = useState([]);
  
  // Update summary and chart when invoice search changes (exact match)
  useEffect(() => {
    if (invoiceSearch) {
      const searchFiltered = filteredSalaries.filter((salary) => 
        (salary.invoice_no || "").toLowerCase() === invoiceSearch.toLowerCase().trim()
      );
      updateFilteredSummary(searchFiltered);
      updateChart(searchFiltered);
    } else {
      updateFilteredSummary(filteredSalaries);
      updateChart(filteredSalaries);
    }
  }, [invoiceSearch, filteredSalaries, selectedSalaryIds]); // Added selectedSalaryIds dependency
  
  // Refetch gap metrics when invoice search or filters change
  useEffect(() => {
    console.log('🎯 Gap Metrics useEffect triggered!', {
      invoiceSearch,
      filtersState: filters.state,
      filtersMonth: filters.month,
      filteredSalariesCount: filteredSalaries.length,
      refreshTrigger
    });
    
    const fetchGapMetrics = async () => {
      try {
        setGapMetricsLoading(true);
        const queryParams = new URLSearchParams();
        if (filters.state) queryParams.append('state', filters.state);
        if (filters.month) queryParams.append('month', filters.month);
        if (filters.year) queryParams.append('year', filters.year);
        if (filters.vc) queryParams.append('vc_name', filters.vc);

        // Priority 1: If rows are SELECTED, use only selected rows
        if (selectedSalaryIds.length > 0) {
          const selectedInvoiceNumbers = filteredSalaries
            .filter(salary => selectedSalaryIds.includes(salary._id))
            .map(salary => salary.invoice_no)
            .filter(Boolean);
          
          console.log('✅ Using SELECTED salary rows:', selectedInvoiceNumbers.length, 'invoices');
          
          if (selectedInvoiceNumbers.length > 0) {
            queryParams.append('invoice_numbers', selectedInvoiceNumbers.join(','));
          }
        }
        // Priority 2: If invoice search is active (and no rows selected)
        else if (invoiceSearch) {
          const filteredInvoiceNumbers = filteredSalaries
            .filter((salary) => 
              (salary.invoice_no || "").toLowerCase() === invoiceSearch.toLowerCase().trim()
            )
            .map((salary) => salary.invoice_no)
            .filter(Boolean);
          
          console.log('🔍 Searching for invoice numbers:', filteredInvoiceNumbers);
          
          if (filteredInvoiceNumbers.length > 0) {
            queryParams.append('invoice_numbers', filteredInvoiceNumbers.join(','));
          } else {
            // If invoice search is active but no matches found, send a dummy value
            console.warn('⚠️ No matching invoices found for search:', invoiceSearch);
            queryParams.append('invoice_numbers', '__NO_MATCH__');
          }
        }
        // Priority 3: If we have filtered salaries (state/month/year filter applied), send those invoice numbers
        else if (filteredSalaries && filteredSalaries.length > 0) {
          const allFilteredInvoiceNumbers = filteredSalaries
            .map(salary => salary.invoice_no)
            .filter(Boolean);
          
          console.log('📊 Using ALL filtered salary records:', allFilteredInvoiceNumbers.length, 'invoices');
          
          if (allFilteredInvoiceNumbers.length > 0) {
            queryParams.append('invoice_numbers', allFilteredInvoiceNumbers.join(','));
          }
        }
        // Priority 4: No data - will use filters only

        const url = `${base_url}invoince/gapMetrics?${queryParams.toString()}`;
        console.log('📡 Fetching gap metrics from:', url);

        const response = await axios.get(url, config);

        console.log('📊 GAP METRICS RESPONSE:', response.data);

        if (response.data.success) {
          console.log('✅ Updating gap metrics state with:', response.data.data);
          setGapMetrics({
            ...response.data.data,
            _timestamp: Date.now(),
          });
        } else {
          console.warn('⚠️ Gap metrics API returned unsuccessful response');
        }
      } catch (error) {
        console.error("❌ Failed to fetch gap metrics:", error);
      } finally {
        setGapMetricsLoading(false);
      }
    };
    
    // Always call fetchGapMetrics, even if filteredSalaries is empty (for invoice search with no results)
    console.log('✅ Calling fetchGapMetrics...');
    fetchGapMetrics();
  }, [invoiceSearch, filters.state, filters.month, filters.year, filters.vc, filteredSalaries, selectedSalaryIds, refreshTrigger]);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [loading, setLoading] = useState(false);
  const [districtOptions, setDistrictOptions] = useState([]);
  const [vcOptions, setVcOptions] = useState([]);
  const [selectedFile, setSelectedFile] = useState(null);
  const [editingRow, setEditingRow] = useState(null);
  const [editableData, setEditableData] = useState({});
  const [sortConfig, setSortConfig] = useState({ key: null, direction: "asc" });
  const [viewModalOpen, setViewModalOpen] = useState(false);
  const [viewingSalary, setViewingSalary] = useState(null);
  const [editModalOpen, setEditModalOpen] = useState(false);
  const [editingSalary, setEditingSalary] = useState(null);
  const [bulkDeleteModal, setBulkDeleteModal] = useState({ open: false, type: '' });
  const [csvValidationErrors, setCsvValidationErrors] = useState(null);

  const projectOptions = ["NSQF"];

  useEffect(() => {
    dispatch(getSalary({})); // Pass empty object for initial load
    dispatch(getStates());
    dispatch(getUsers());
  }, [dispatch]);

  const getDistrictsForState = () => {
    if (!formData.state || !statelist) return [];
    const selectedState = statelist.find(
      (state) => state.state_name === formData.state
    );
    return selectedState ? selectedState.district_name : [];
  };

  const getVCsForState = () => {
    if (!formData.state || !vclist) return [];
    const normalizedState =
      formData.state.match(/\(([^)]+)\)/)?.[1] || formData.state;
    const filteredVCs = vclist.filter(
      (vc) => vc.role === "vc" && vc.state === normalizedState
    );
    return filteredVCs;
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    let newValue = value;

    if (name === "salaryDue" || name === "additionalDueAmount") {
      newValue = value === "" ? "" : Number(value);
    }

    if (name === "state") {
      setFormData((prev) => ({
        ...prev,
        state: value,
        district: "",
        vc: "",
      }));
      const districts = getDistrictsForState();
      setDistrictOptions(districts);
      setVcOptions(getVCsForState());
    } else if (name === "district") {
      setFormData((prev) => ({
        ...prev,
        district: value,
        vc: "",
      }));
      setVcOptions(getVCsForState());
    } else {
      setFormData((prev) => ({
        ...prev,
        [name]: newValue,
      }));
    }
  };

  useEffect(() => {
    if (isLoading) {
      setFilteredSalaries([]);
      setFilteredSummary({
        totalSalaryDue: 0,
        totalPaidSalary: 0,
        totalAdjustments: 0,
        totalUnpaidSalary: 0,
        totalDeductions: 0,
      });
      return;
    }

    if (Array.isArray(salaries) && salaries.length > 0) {
      const normalizedSalaries = salaries.map((salary) => ({
        ...salary,
        state_name: salary.state_name || salary.state || "N/A",
        vc_name: salary.vc_name || salary.vc || "N/A",
        district: salary.district || "N/A",
        total_salary_paid: salary.total_salary_paid || 0,
      }));
      setFilteredSalaries(normalizedSalaries);
      updateFilteredSummary(normalizedSalaries);
      updateChart(normalizedSalaries);
    } else {
      setFilteredSalaries([]);
      setFilteredSummary({
        totalSalaryDue: 0,
        totalPaidSalary: 0,
        totalAdjustments: 0,
        totalUnpaidSalary: 0,
        totalDeductions: 0,
      });
      updateChart([]);
    }
  }, [salaries, isLoading]);

  const updateFilteredSummary = (data) => {
    if (!Array.isArray(data)) {
      setFilteredSummary({
        totalSalaryDue: 0,
        totalPaidSalary: 0,
        totalAdjustments: 0,
        totalUnpaidSalary: 0,
        totalDeductions: 0,
      });
      return;
    }

    // If rows are selected, calculate summary for selected rows only
    const dataToCalculate = selectedSalaryIds.length > 0 
      ? data.filter(salary => selectedSalaryIds.includes(salary._id))
      : data;

    const totalSalaryDue = dataToCalculate.reduce(
      (sum, salary) => sum + (Number(salary.salaryDue) || 0),
      0
    );
    const totalPaidSalary = dataToCalculate.reduce(
      (sum, salary) =>
        sum +
        (Number(salary.salaryPaid) || 0) +
        (Number(salary.tds_deducted) || 0),
      0
    );
    const totalAdjustments = dataToCalculate.reduce(
      (sum, salary) => sum + (Number(salary.adjustments) || 0),
      0
    );
    const totalUnpaidSalary = dataToCalculate.reduce(
      (sum, salary) => sum + (Number(salary.unpaid_salary) || 0),
      0
    );
    const totalDeductions = dataToCalculate.reduce(
      (sum, salary) => sum + (Number(salary.tds_deducted) || 0),
      0
    );

    const validatedTotalPaidSalary = Math.min(totalPaidSalary, totalSalaryDue);

    setFilteredSummary({
      totalSalaryDue,
      totalPaidSalary: validatedTotalPaidSalary,
      totalAdjustments,
      totalUnpaidSalary,
      totalDeductions,
    });
  };

  const normalizeState = (state) => {
    if (!state) return { display: "N/A", code: "N/A" };
    const match = state.match(/^(.*?)\s*\((.*?)\)$/);
    if (match) {
      return { display: state, code: match[2].trim() };
    }
    return { display: state, code: state };
  };

  const updateChart = (chartData) => {
    const ctx = chartRef.current?.getContext("2d");
    if (!ctx) return;
    if (chartInstanceRef.current) chartInstanceRef.current.destroy();

    // Use selected salaries if any are selected, otherwise use all chartData
    const dataToChart = selectedSalaryIds.length > 0
      ? chartData.filter(salary => selectedSalaryIds.includes(salary._id))
      : chartData;

    const totalSalaryDue = Array.isArray(dataToChart)
      ? dataToChart.reduce(
          (sum, salary) => sum + (Number(salary.salaryDue) || 0),
          0
        )
      : 0;
    const totalSalaryPaid = Array.isArray(dataToChart)
      ? dataToChart.reduce(
          (sum, salary) => sum + (Number(salary.total_salary_paid) || 0),
          0
        )
      : 0;
    const totalAdjustments = Array.isArray(dataToChart)
      ? dataToChart.reduce(
          (sum, salary) => sum + (Number(salary.adjustments) || 0),
          0
        )
      : 0;
    const totalUnpaidSalary = Array.isArray(dataToChart)
      ? dataToChart.reduce(
          (sum, salary) => sum + (Number(salary.unpaid_salary) || 0),
          0
        )
      : 0;

    const validatedTotalSalaryPaid = Math.min(totalSalaryPaid, totalSalaryDue);

    chartInstanceRef.current = new Chart(ctx, {
      type: "bar",
      data: {
        labels: [
          "Total Salary Due",
          "Total Salary Paid",
          "Total Adjustments",
          "Total Unpaid Salary",
        ],
        datasets: [
          {
            label: "Salary Metrics (₹)",
            data: [
              totalSalaryDue,
              validatedTotalSalaryPaid,
              totalAdjustments,
              totalUnpaidSalary,
            ],
            backgroundColor: ["#3b82f6", "#22c55e", "#f59e0b", "#ef4444"],
          },
        ],
      },
      options: {
        responsive: true,
        maintainAspectRatio: false,
        plugins: {
          legend: { display: false },
          tooltip: {
            callbacks: {
              label: (tooltipItem) =>
                `${tooltipItem.label}: ₹${tooltipItem.raw.toLocaleString()}`,
            },
          },
        },
        scales: {
          x: { title: { display: true, text: "Metrics" } },
          y: {
            title: { display: true, text: "Amount (₹)" },
            beginAtZero: true,
            ticks: { callback: (value) => `₹${value.toLocaleString()}` },
          },
        },
      },
    });
    dispatch(csvDownload());
  };

  // Selection handlers for checkboxes
  const handleSelectSalary = (id) => {
    setSelectedSalaryIds(prev => 
      prev.includes(id) ? prev.filter(x => x !== id) : [...prev, id]
    );
  };

  const handleSelectAllSalaries = (e) => {
    if (e.target.checked) {
      // Select all current page salaries
      const currentPageIds = paginatedRows.map(row => row.id);
      setSelectedSalaryIds(prev => [...new Set([...prev, ...currentPageIds])]);
    } else {
      // Deselect all current page salaries
      const currentPageIds = paginatedRows.map(row => row.id);
      setSelectedSalaryIds(prev => prev.filter(id => !currentPageIds.includes(id)));
    }
  };

  const handleBulkDelete = (type) => {
    if (type === 'selected' && selectedSalaryIds.length === 0) {
      toast.warning("Please select at least one salary record to delete.", { autoClose: 3000 });
      return;
    }
    setBulkDeleteModal({ open: true, type });
  };

  const confirmBulkDelete = () => {
    setBulkDeleteModal({ open: false, type: '' });

    const idsToDelete = selectedSalaryIds;

    if (idsToDelete.length === 0) {
      toast.warning("No salary records to delete.", { autoClose: 3000 });
      return;
    }

    axios.delete(`${base_url}salryrecord/bulkDeleteSalaries`, {
      ...config,
      data: { salary_ids: idsToDelete }
    })
      .then(() => {
        toast.success(`Successfully deleted ${idsToDelete.length} salary record(s)!`, { autoClose: 3000 });
        setSelectedSalaryIds([]);
        dispatch(getSalary());
      })
      .catch((error) => {
        console.error("Failed to bulk delete salaries:", error);
        toast.error(`Failed to delete salaries: ${error.response?.data?.msg || error.message || "Please try again."}`, { autoClose: 3000 });
      });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    const payload = {
      ...formData,
      district: formData.district || "N/A",
      vc: formData.vc,
    };

    try {
      const res = await dispatch(createMonthSalary(payload)).unwrap();
      if (res?.message && res?.data) {
        toast.success("Salary record created successfully!");
        const newSalary = { ...res.data, vc_name: formData.vc };
        const updatedSalaries = Array.isArray(salaries)
          ? [...salaries, newSalary]
          : [newSalary];

        const filtered = updatedSalaries.filter((salary) => {
          const monthMatch = !filters.month || salary.month === filters.month;
          const yearMatch = !filters.year || salary.year === filters.year;
          const stateMatch =
            !filters.state ||
            (salary.state_name || salary.state) === filters.state;
          const districtMatch =
            !filters.district || salary.district === filters.district;
          const vcMatch = !filters.vc || salary.vc_name === filters.vc;
          const vtMatch = !filters.vt || salary.vt === filters.vt;

          return (
            monthMatch &&
            yearMatch &&
            stateMatch &&
            districtMatch &&
            vcMatch &&
            vtMatch
          );
        });

        setFilteredSalaries(filtered);
        updateFilteredSummary(filtered);
        updateChart(filtered);

        setIsModalOpen(false);
        setFormData({
          month: "",
          year: "",
          state: "",
          district: "",
          vc: "",
          salaryDue: 0,
          additionalDueAmount: 0,
        });
        setDistrictOptions([]);
        setVcOptions([]);
        dispatch(getSalary());
      } else {
        toast.error(
          `Failed to create salary record: ${
            res?.error?.message || "Unknown error"
          }`
        );
      }
    } catch (err) {
      toast.error(`Error during submission: ${err.message}`);
      setIsModalOpen(false);
    }
  };

  const downloadCsv = () => {
    if (csvData) {
      const blob = new Blob([csvData], { type: "text/csv;charset=utf-8;" });
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = url;
      link.setAttribute("download", "salary_template.csv");
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
    } else {
      toast.warning("No CSV data available to download.", { autoClose: 3000 });
    }
  };

  const exportAsCSV = () => {
    const headers = [
      "Invoice Number",
      "Month",
      "Year",
      "State",
      "District",
      "vc_name",
      "Salary Due Date",
      "Salary Paid Date",
      "Salary Due (₹)",
      "Additional Due Amount (₹)",
      "Total Due Amount (₹)",
      "Salary Paid (₹)",
      "Additional Paid Amount (₹)",
      "Total Paid Amount (₹)",
      "Deductions (₹)",
      "Adjustments (₹)",
      "Total Salary Paid (₹)",
      "Unpaid Salary (₹)",
      "Created by",
      "Remarks",
    ];

    const csvData = filteredSalaries.map((salary) => {
      const monthNames = ["January", "February", "March", "April", "May", "June", "July", "August", "September", "October", "November", "December"];
      const monthIndex = monthNames.indexOf(salary?.month);
      let salaryDueDate = "N/A";
      if (monthIndex !== -1 && salary?.year) {
        const year = parseInt(salary.year);
        const lastDate = new Date(year, monthIndex + 1, 0);
        // Format as YYYY-MM-DD in local timezone
        const yyyy = lastDate.getFullYear();
        const mm = String(lastDate.getMonth() + 1).padStart(2, '0');
        const dd = String(lastDate.getDate()).padStart(2, '0');
        salaryDueDate = `${yyyy}-${mm}-${dd}`;
      }
      
      return [
        salary?.invoice_no || "N/A",
        salary?.month || "N/A",
        salary?.year || "N/A",
        salary?.state_name || "N/A",
        salary?.district || "N/A",
        salary?.vc_name || "N/A",
        salaryDueDate,
        salary?.salaryPaidDate ? new Date(salary.salaryPaidDate).toISOString().split('T')[0] : "N/A",
        salary?.salaryDue || 0,
        salary?.additionalDueAmount || 0,
        salary?.total_due_amount || 0,
        salary?.salaryPaid || 0,
        salary?.additionalPaidAmount || 0,
        salary?.total_paid_amount || 0,
        salary?.tds_deducted || 0,
        salary?.adjustments || 0,
        salary?.total_salary_paid || 0,
        salary?.unpaid_salary || 0,
        salary?.created_by_details?.email || " ",
        salary?.remarks || "N/A",
      ];
    });

    const csvContent = [
      headers.join(","),
      ...csvData.map((row) => row.map((cell) => `"${cell}"`).join(",")),
    ].join("\n");

    const blob = new Blob([csvContent], { type: "text/csv;charset=utf-8;" });
    const url = window.URL.createObjectURL(blob);
    const link = document.createElement("a");
    link.href = url;
    link.setAttribute("download", "salary_data.csv");
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    window.URL.revokeObjectURL(url);
  };

  const exportAsPDF = () => {
    const doc = new jsPDF("landscape", "mm", "a4");
    
    // Add font support
    doc.setFont("helvetica");

    doc.setFontSize(16);
    doc.text("Salary Payment Dashboard", 14, 10);

    const formatPDFCurrency = (value) => {
      if (value === null || value === undefined || value === "") return "Rs 0";
      const num = parseFloat(value);
      if (isNaN(num)) return "Rs 0";
      return `Rs ${num.toFixed(0)}`;
    };

    const cardData = [
      [
        "Total Salary Due",
        formatPDFCurrency(filteredSummary.totalSalaryDue),
      ],
      [
        "Total Salary Paid",
        formatPDFCurrency(filteredSummary.totalPaidSalary),
      ],
      [
        "Total Adjustments",
        formatPDFCurrency(filteredSummary.totalAdjustments),
      ],
      [
        "Total Unpaid Salary",
        formatPDFCurrency(filteredSummary.totalUnpaidSalary),
      ],
    ];
    doc.autoTable({
      startY: 15,
      head: [["Metric", "Value"]],
      body: cardData,
      theme: "grid",
      styles: { fontSize: 10, cellPadding: 2, font: "helvetica" },
      headStyles: { fillColor: [66, 66, 66] },
      columnStyles: { 0: { cellWidth: 50 }, 1: { cellWidth: 100 } },
    });

    const chartCanvas = chartRef.current;
    let chartAdded = false;
    if (chartCanvas) {
      try {
        const chartImage = chartCanvas.toDataURL("image/png");
        doc.addImage(chartImage, "PNG", 14, doc.lastAutoTable.finalY + 5, 270, 60);
        chartAdded = true;
      } catch (err) {
        console.warn("Chart image not available for PDF:", err);
      }
    }

    const headers = [
      "Invoice Number",
      "Month",
      "Year",
      "State",
      "District",
      "vc_name",
      "Salary Due Date",
      "Salary Paid Date",
      "Salary Due",
      "Additional Due Amount",
      "Total Due Amount",
      "Salary Paid",
      "Additional Paid Amount",
      "Total Paid Amount",
      "Deductions",
      "Adjustments",
      "Total Salary Paid",
      "Unpaid Salary",
      "Create by",
      "Remarks",
    ];

    const tableData = filteredSalaries.map((salary) => {
      const monthNames = ["January", "February", "March", "April", "May", "June", "July", "August", "September", "October", "November", "December"];
      const monthIndex = monthNames.indexOf(salary?.month);
      let salaryDueDate = "N/A";
      if (monthIndex !== -1 && salary?.year) {
        const year = parseInt(salary.year);
        const lastDate = new Date(year, monthIndex + 1, 0);
        // Format as YYYY-MM-DD in local timezone
        const yyyy = lastDate.getFullYear();
        const mm = String(lastDate.getMonth() + 1).padStart(2, '0');
        const dd = String(lastDate.getDate()).padStart(2, '0');
        salaryDueDate = `${yyyy}-${mm}-${dd}`;
      }
      
      return [
        salary?.invoice_no || "N/A",
        salary?.month || "N/A",
        salary?.year || "N/A",
        salary?.state_name || "N/A",
        salary?.district || "N/A",
        salary?.vc_name || "N/A",
        salaryDueDate,
        salary?.salaryPaidDate ? new Date(salary.salaryPaidDate).toISOString().split('T')[0] : "N/A",
        formatPDFCurrency(salary?.salaryDue || 0),
        formatPDFCurrency(salary?.additionalDueAmount || 0),
        formatPDFCurrency(salary?.total_due_amount || 0),
        formatPDFCurrency(salary?.salaryPaid || 0),
        formatPDFCurrency(salary?.additionalPaidAmount || 0),
        formatPDFCurrency(salary?.total_paid_amount || 0),
        formatPDFCurrency(salary?.tds_deducted || 0),
        formatPDFCurrency(salary?.adjustments || 0),
        formatPDFCurrency(salary?.total_salary_paid || 0),
        formatPDFCurrency(salary?.unpaid_salary || 0),
        salary?.created_by_details?.email || " ",
        salary?.remarks || "N/A",
      ];
    });

    doc.autoTable({
      startY: chartAdded ? doc.lastAutoTable.finalY + 68 : doc.lastAutoTable.finalY + 8,
      head: [headers],
      body: tableData,
      theme: "striped",
      styles: { 
        fontSize: 5.5, 
        cellPadding: 1,
        font: "helvetica",
        overflow: "linebreak",
        cellWidth: "wrap",
        halign: "left"
      },
      headStyles: {
        fillColor: [41, 128, 185],
        textColor: 255,
        fontSize: 5.5,
        fontStyle: "bold",
        halign: "center"
      },
      margin: { left: 5, right: 5 },
      columnStyles: {
        0: { cellWidth: 18 },  // Invoice Number
        1: { cellWidth: 12 },  // Month
        2: { cellWidth: 8 },   // Year
        3: { cellWidth: 14 },  // State
        4: { cellWidth: 10 },  // District
        5: { cellWidth: 16 },  // vc_name
        6: { cellWidth: 14 },  // Salary Due Date
        7: { cellWidth: 14 },  // Salary Paid Date
        8: { cellWidth: 13 },  // Salary Due
        9: { cellWidth: 13 },  // Additional Due
        10: { cellWidth: 13 }, // Total Due
        11: { cellWidth: 13 }, // Salary Paid
        12: { cellWidth: 13 }, // Additional Paid
        13: { cellWidth: 13 }, // Total Paid
        14: { cellWidth: 11 }, // Deductions
        15: { cellWidth: 11 }, // Adjustments
        16: { cellWidth: 13 }, // Total Salary Paid
        17: { cellWidth: 11 }, // Unpaid Salary
        18: { cellWidth: 20 }, // Create by
        19: { cellWidth: 15 }  // Remarks
      },
      didDrawPage: (data) => {
        doc.setFontSize(8);
        const pageWidth = doc.internal.pageSize.getWidth();
        const pageHeight = doc.internal.pageSize.getHeight();
        doc.text(
          `Page ${data.pageNumber}`,
          pageWidth / 2,
          pageHeight - 10,
          { align: "center" }
        );
      },
    });

    doc.save("salary_payment_report.pdf");
  };

  const handleFileChange = (e) => {
    const file = e.target.files[0];
    setSelectedFile(file);
  };

  const handleImport = () => {
    if (!selectedFile) {
      toast.warning("Please select a CSV file to upload.", { autoClose: 3000 });
      return;
    }
    if (selectedFile.size === 0) {
      toast.error("The selected CSV file is empty. Please upload a valid file.", { autoClose: 3000 });
      return;
    }
    if (!selectedFile.name.endsWith(".csv")) {
      toast.error("Please upload a valid CSV file.", { autoClose: 3000 });
      return;
    }

    const formData = new FormData();
    formData.append("salaryFile", selectedFile);

    dispatch(uploadCsv(formData))
      .unwrap()
      .then((response) => {
        if (response.message) {
          toast.success(response.message || "CSV file uploaded successfully!", { autoClose: 3000 });
          dispatch(getSalary());
          setSelectedFile(null);
          const fileInput = document.getElementById('file-upload-new');
          if (fileInput) fileInput.value = '';
        } else {
          toast.error(`Upload failed: ${response.message || "Unknown error"}`, { autoClose: 3000 });
        }
      })
      .catch((err) => {
        console.error("Error uploading CSV:", err);
        if (err?.errors && Array.isArray(err.errors) && err.errors.length > 0) {
          setCsvValidationErrors(err);
          toast.error(`CSV Pre-Validation Failed! Found ${err.errorCount || err.errors.length} issue(s). 0 records saved.`, { autoClose: 5000 });
        } else {
          const errorMessage = err?.error || err?.message || err?.msg || "Please try again.";
          toast.error(`Failed to upload CSV: ${errorMessage}`, { autoClose: 5000 });
        }
      });
  };

  const handleFilterChange = (key, value) => {
    setFilters((prev) => ({ ...prev, [key]: value }));
  };

  const applyFilters = () => {
    console.log('🔍 Apply Filters Clicked!');
    console.log('📅 Current Filters:', filters);
    console.log('📅 From Date:', filters.fromDate);
    console.log('📅 To Date:', filters.toDate);
    
    if (!Array.isArray(salaries)) {
      console.warn('⚠️ Salaries is not an array, resetting...');
      setFilteredSalaries([]);
      updateFilteredSummary([]);
      updateChart([]);
      setPage(0);
      return;
    }

    // Build query object for backend
    const query = {
      month: filters.month,
      year: filters.year,
      state: filters.state,
      district: filters.district,
      vc_name: filters.vc,
      fromDate: filters.fromDate,
      toDate: filters.toDate,
    };

    console.log('📦 Query Object:', query);

    // Remove empty values
    const filteredQuery = Object.fromEntries(
      Object.entries(query).filter(
        ([_, value]) => value !== "" && value !== undefined && value !== null
      )
    );

    console.log('✅ Filtered Query (sending to backend):', filteredQuery);
    console.log('🌐 API URL:', `${base_url}salryrecord/?${new URLSearchParams(filteredQuery).toString()}`);

    // Fetch filtered data from backend
    dispatch(getSalary(filteredQuery))
      .unwrap()
      .then((response) => {
        console.log('📊 Response from backend:', response);
        
        if (!response || !response.salaries) {
          console.error('❌ Invalid response format:', response);
          toast.error("Invalid response from server. Please try again.");
          return;
        }
        
        const salariesData = response.salaries;
        
        if (salariesData === "No records found") {
          console.log('ℹ️ No records found message received');
          setFilteredSalaries([]);
          updateFilteredSummary([]);
          updateChart([]);
          setPage(0);
          toast.info("No records found for the selected filters.");
          return;
        }
        
        if (!Array.isArray(salariesData)) {
          console.error('❌ Salaries data is not an array:', salariesData);
          toast.error("Invalid data format received. Please try again.");
          return;
        }
        
        const normalizedSalaries = salariesData.map((salary) => ({
          ...salary,
          state_name: salary.state_name || salary.state || "N/A",
          vc_name: salary.vc_name || salary.vc || "N/A",
          district: salary.district || "N/A",
          total_salary_paid: salary.total_salary_paid || 0,
        }));
        
        console.log('✅ Normalized Salaries Count:', normalizedSalaries.length);
        setFilteredSalaries(normalizedSalaries);
        updateFilteredSummary(normalizedSalaries);
        updateChart(normalizedSalaries);
        setPage(0);
        
        if (normalizedSalaries.length > 0) {
          toast.success(`Found ${normalizedSalaries.length} record(s)`);
        } else {
          toast.info("No records found for the selected filters.");
        }
      })
      .catch((error) => {
        console.error("❌ Failed to fetch filtered salaries:", error);
        console.error("❌ Error details:", error.response?.data || error.message);
        toast.error(`Failed to apply filters: ${error.response?.data?.message || error.message || "Please try again."}`);
      });
  };

  const resetFilters = () => {
    setFilters({
      month: "",
      year: "",
      state: "",
      district: "",
      vc: "",
      vt: "",
      fromDate: "",
      toDate: "",
    });
    
    // Refetch all data without filters
    dispatch(getSalary())
      .unwrap()
      .then((response) => {
        const salariesData = response?.salaries || [];
        const normalizedSalaries = salariesData.map((salary) => ({
          ...salary,
          state_name: salary.state_name || salary.state || "N/A",
          vc_name: salary.vc_name || salary.vc || "N/A",
          district: salary.district || "N/A",
          total_salary_paid: salary.total_salary_paid || 0,
        }));
        setFilteredSalaries(normalizedSalaries);
        updateFilteredSummary(normalizedSalaries);
        updateChart(normalizedSalaries);
        setPage(0);
      })
      .catch((error) => {
        console.error("Failed to reset filters:", error);
      });
  };

  const handleViewRow = (salary) => {
    setViewingSalary(salary);
    setViewModalOpen(true);
  };

  const handleEdit = (salary) => {
    console.log('🔧 Edit button clicked!', salary);
    console.log('📝 Setting editingSalary state');
    setEditingSalary({...salary});
    console.log('🔓 Opening modal - setEditModalOpen(true)');
    setEditModalOpen(true);
    console.log('✅ Modal should be open now');
  };

  const handleEditSubmit = async () => {
    try {
      const payload = {
        invoice_no: editingSalary.invoice_no || "",
        salaryPaid: editingSalary.salaryPaid || 0,
        salaryPaidDate: editingSalary.salaryPaidDate || "",
        additionalPaidAmount: editingSalary.additionalPaidAmount || 0,
        tds_deducted: editingSalary.tds_deducted || 0,
        adjustments: editingSalary.adjustments || 0,
        remarks: editingSalary.remarks || "",
      };

      // Superadmin can update additional fields
      if (user?.role === "superadmin") {
        payload.month = editingSalary.month;
        payload.year = editingSalary.year;
        payload.state = editingSalary.state;
        payload.district = editingSalary.district;
        payload.vc_name = editingSalary.vc_name;
        payload.salaryDue = editingSalary.salaryDue;
        payload.additionalDueAmount = editingSalary.additionalDueAmount;
      }

      const response = await axios.put(
        `${base_url}salryrecord/updatePaidSalary?id=${editingSalary._id}`,
        payload,
        config
      );
      
      toast.success(`Salary record updated successfully!`, { autoClose: 3000 });
      setEditModalOpen(false);
      setEditingSalary(null);
      dispatch(getSalary());
    } catch (error) {
      toast.error(error.response?.data?.error || "Failed to update salary record");
    }
  };


  const handleEditRow = (id) => {
    setEditingRow(id);
    const salary = filteredSalaries.find((s) => s._id === id);
    console.log('📝 EDITING SALARY RECORD:', salary);
    console.log('📅 EXISTING SALARY PAID DATE:', salary.salaryPaidDate);
    setEditableData({
      invoiceNumber: salary.invoice_no || "",
      salaryPaid: salary.salaryPaid || 0,
      salaryPaidDate: salary.salaryPaidDate ? new Date(salary.salaryPaidDate).toISOString().split('T')[0] : "",
      tdsDeducted: salary.tds_deducted || 0,
      adjustments: salary.adjustments || 0,
      additionalPaidAmount: salary.additionalPaidAmount || 0,
      remarks: salary.remarks || "",
    });
  };

  const handleEditableChange = (e, field) => {
    setEditableData({ ...editableData, [field]: e.target.value });
  };

  const handleSaveRow = async (id) => {
    const updatedData = {
      invoice_no: editableData.invoiceNumber || "",
      salaryPaid: parseFloat(editableData.salaryPaid) || 0,
      salaryPaidDate: editableData.salaryPaidDate || "",
      tds_deducted: parseFloat(editableData.tdsDeducted) || 0,
      adjustments: parseFloat(editableData.adjustments) || 0,
      additionalPaidAmount: parseFloat(editableData.additionalPaidAmount) || 0,
      remarks: editableData.remarks || "",
    };

    console.log('📅 FRONTEND - Salary Paid Date:', editableData.salaryPaidDate);
    console.log('📦 FRONTEND - Full Update Data:', updatedData);

    try {
      await dispatch(
        updatePaidSalary({ id, salaryData: updatedData })
      ).unwrap();
      toast.success("Salary updated successfully!");
      setEditingRow(null);
      dispatch(getSalary());
    } catch (error) {
      const errorMessage = error?.response?.data?.error ||      // Backend "error" field
                          error?.response?.data?.message || 
                          error?.response?.data?.msg || 
                          error?.message || 
                          "Request failed with status code 400";
      toast.error(
        `Failed to update salary: ${errorMessage}`,
        { autoClose: 5000 }
      );
    }
  };

  const handleDeleteRow = async (id) => {
    if (window.confirm("Are you sure you want to delete this salary record?")) {
      try {
        await dispatch(deleteSalaryRecord(id)).unwrap();
        toast.success("Salary record deleted successfully!");
        dispatch(getSalary());
      } catch (error) {
        const errorMessage = error?.response?.data?.error ||      // Backend "error" field
                            error?.response?.data?.message || 
                            error?.response?.data?.msg || 
                            error?.message || 
                            "Unknown error";
        toast.error(
          `Failed to delete salary record: ${errorMessage}`,
          { autoClose: 5000 }
        );
      }
    }
  };

  const handleSort = (key, index) => {
    if (!Array.isArray(filteredSalaries) || filteredSalaries.length === 0)
      return;

    const direction =
      sortConfig.key === key && sortConfig.direction === "asc" ? "desc" : "asc";
    setSortConfig({ key, direction });

    const sortedData = [...filteredSalaries].sort((a, b) => {
      let aValue, bValue;

      switch (index) {
        case 0:
          aValue = a.invoice_no || "";
          bValue = b.invoice_no || "";
          break;
        case 1:
          aValue = a.month || "";
          bValue = b.month || "";
          break;
        case 2:
          aValue = a.year || "";
          bValue = b.year || "";
          break;
        case 3:
          aValue = a.state_name || "";
          bValue = b.state_name || "";
          break;
        case 4:
          aValue = a.district || "";
          bValue = b.district || "";
          break;
        case 5:
          aValue = a.vc_name || "";
          bValue = b.vc_name || "";
          break;
        case 6:
          aValue = Number(a.salaryDue) || 0;
          bValue = Number(b.salaryDue) || 0;
          break;
        case 7:
          aValue = Number(a.additionalDueAmount) || 0;
          bValue = Number(b.additionalDueAmount) || 0;
          break;
        case 8:
          aValue = Number(a.total_due_amount) || 0;
          bValue = Number(b.total_due_amount) || 0;
          break;
        case 9:
          aValue = Number(a.salaryPaid) || 0;
          bValue = Number(b.salaryPaid) || 0;
          break;
        case 10:
          aValue = Number(a.additionalPaidAmount) || 0;
          bValue = Number(b.additionalPaidAmount) || 0;
          break;
        case 11:
          aValue = Number(a.total_paid_amount) || 0;
          bValue = Number(b.total_paid_amount) || 0;
          break;
        case 12:
          aValue = Number(a.tds_deducted) || 0;
          bValue = Number(b.tds_deducted) || 0;
          break;
        case 13:
          aValue = Number(a.adjustments) || 0;
          bValue = Number(b.adjustments) || 0;
          break;
        case 14:
          aValue = Number(a.total_salary_paid) || 0;
          bValue = Number(b.total_salary_paid) || 0;
          break;
        case 15:
          aValue = Number(a.unpaid_salary) || 0;
          bValue = Number(b.unpaid_salary) || 0;
          break;
        case 16:
          aValue = a.remarks || "";
          bValue = b.remarks || "";
          break;
        default:
          aValue = "";
          bValue = "";
      }

      if (typeof aValue === "string" && typeof bValue === "string") {
        aValue = aValue.toLowerCase();
        bValue = bValue.toLowerCase();
      }

      if (aValue < bValue) return direction === "asc" ? -1 : 1;
      if (aValue > bValue) return direction === "asc" ? 1 : -1;
      return 0;
    });

    setFilteredSalaries(sortedData);
    setPage(0);
  };
const [hoveredCard, setHoveredCard] = useState(null);
// Enhanced card style
const cardStyle = {
  padding: "20px 16px",
  borderRadius: "12px",
  color: "#ffffff",
  textAlign: "center",
  transition: "all 0.3s cubic-bezier(0.4, 0, 0.2, 1)",
  cursor: "pointer",
  position: "relative",
  overflow: "hidden",
  minHeight: "110px",
  display: "flex",
  flexDirection: "column",
  justifyContent: "center",
  alignItems: "center",
  border: "1px solid rgba(255, 255, 255, 0.15)",
  boxShadow: "0 2px 8px rgba(0, 0, 0, 0.08)",
};
// Grid style with 5 columns

  const uniqueMonths = Array.isArray(salaries)
    ? Array.from(new Set(salaries.map((salary) => salary.month || "N/A")))
    : [];
  const uniqueYears = Array.isArray(salaries)
    ? Array.from(new Set(salaries.map((salary) => salary.year || "N/A")))
    : [];
  const uniqueStates = Array.isArray(salaries)
    ? Array.from(
        new Set(
          salaries.flatMap((salary) => {
            const state = salary.state_name || salary.state || "N/A";
            const normalized = normalizeState(state);
            return [normalized.display, normalized.code].filter(
              (s) => s !== "N/A"
            );
          })
        )
      ).sort()
    : [];
  const uniqueDistricts = Array.isArray(salaries)
    ? Array.from(new Set(salaries.map((salary) => salary.district || "N/A")))
    : [];
  const uniqueVCs = Array.isArray(salaries)
    ? Array.from(new Set(salaries.map((salary) => salary.vc_name || "N/A")))
    : [];
  const uniqueVTs = Array.isArray(salaries)
    ? Array.from(new Set(salaries.map((salary) => salary.vt || "N/A")))
    : [];

    // Add this function before the return statement
const handlePrint = () => {
  // Get the current date for the report
  const currentDate = new Date().toLocaleDateString('en-IN', {
    day: '2-digit',
    month: '2-digit',
    year: 'numeric'
  });

  // Create print content
  const printContent = `
    <!DOCTYPE html>
    <html>
    <head>
      <title>Salary Payment Report</title>
      <style>
        * {
          margin: 0;
          padding: 0;
          box-sizing: border-box;
        }
        body {
          font-family: 'Inter', -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, Arial, sans-serif;
          padding: 40px;
          background: #ffffff;
          color: #1a2332;
        }
        .report-header {
          margin-bottom: 30px;
          padding-bottom: 20px;
          border-bottom: 2px solid #eef2f6;
        }
        .report-title {
          font-size: 24px;
          font-weight: 700;
          color: #1a2332;
          margin-bottom: 4px;
        }
        .report-subtitle {
          font-size: 14px;
          color: #64748b;
        }
        .report-meta {
          display: flex;
          gap: 20px;
          margin-top: 8px;
          font-size: 13px;
          color: #475569;
        }
        .summary-grid {
          display: grid;
          grid-template-columns: repeat(5, 1fr);
          gap: 16px;
          margin: 24px 0;
        }
        .summary-card {
          background: #f8fafc;
          padding: 16px;
          border-radius: 8px;
          border: 1px solid #eef2f6;
          text-align: center;
        }
        .summary-label {
          font-size: 12px;
          color: #64748b;
          font-weight: 500;
          text-transform: uppercase;
          letter-spacing: 0.03em;
        }
        .summary-value {
          font-size: 18px;
          font-weight: 700;
          color: #1a2332;
          margin-top: 4px;
        }
        .summary-value.blue { color: #4a90d9; }
        .summary-value.green { color: #10b981; }
        .summary-value.purple { color: #8b5cf6; }
        .summary-value.orange { color: #ed8936; }
        .summary-value.red { color: #ef4444; }
        
        .gap-metrics {
          display: grid;
          grid-template-columns: repeat(3, 1fr);
          gap: 16px;
          margin: 24px 0;
        }
        .metric-card {
          background: #f8fafc;
          padding: 16px;
          border-radius: 8px;
          border: 1px solid #eef2f6;
          text-align: center;
        }
        .metric-label {
          font-size: 12px;
          color: #64748b;
          font-weight: 500;
        }
        .metric-value {
          font-size: 18px;
          font-weight: 700;
          color: #1a2332;
          margin-top: 4px;
        }
        
        .table-container {
          margin-top: 24px;
          overflow-x: auto;
        }
        table {
          width: 100%;
          border-collapse: collapse;
          font-size: 11px;
        }
        thead {
          background: #f8fafc;
        }
        th {
          padding: 10px 12px;
          text-align: left;
          font-weight: 600;
          color: #475569;
          border-bottom: 2px solid #eef2f6;
          white-space: nowrap;
          text-transform: uppercase;
          font-size: 10px;
          letter-spacing: 0.03em;
        }
        td {
          padding: 8px 12px;
          border-bottom: 1px solid #f0f0f0;
          color: #1a2332;
        }
        tr:hover {
          background: #fafbfc;
        }
        .text-right {
          text-align: right;
        }
        .footer {
          margin-top: 30px;
          padding-top: 20px;
          border-top: 1px solid #eef2f6;
          display: flex;
          justify-content: space-between;
          font-size: 12px;
          color: #94a3b8;
        }
        @media print {
          body { padding: 20px; }
          .no-print { display: none; }
          .summary-grid { break-inside: avoid; }
          .gap-metrics { break-inside: avoid; }
          table { break-inside: auto; }
          tr { break-inside: avoid; }
        }
        @media (max-width: 768px) {
          .summary-grid { grid-template-columns: repeat(2, 1fr); }
          .gap-metrics { grid-template-columns: 1fr; }
        }
      </style>
    </head>
    <body>
      <!-- Report Header -->
      <div class="report-header">
        <div class="report-title">📊 Salary Payment Report</div>
        <div class="report-subtitle">Comprehensive salary summary and details</div>
        <div class="report-meta">
          <span>📅 Generated: ${currentDate}</span>
          <span>📋 Records: ${filteredSalaries.length}</span>
          <span>💰 Total Amount: ₹${filteredSummary.totalSalaryDue.toLocaleString()}</span>
        </div>
      </div>

      <!-- Summary Cards -->
      <div class="summary-grid">
        ${[
          { label: "Total Salary Due", value: `₹${filteredSummary.totalSalaryDue.toLocaleString()}`, color: "blue" },
          { label: "Total Salary Paid", value: `₹${filteredSummary.totalPaidSalary.toLocaleString()}`, color: "green" },
          { label: "Deductions", value: `₹${filteredSummary.totalDeductions.toLocaleString()}`, color: "purple" },
          { label: "Total Adjustments", value: `₹${filteredSummary.totalAdjustments.toLocaleString()}`, color: "orange" },
          { label: "Total Unpaid Salary", value: `₹${filteredSummary.totalUnpaidSalary.toLocaleString()}`, color: "red" }
        ].map(item => `
          <div class="summary-card">
            <div class="summary-label">${item.label}</div>
            <div class="summary-value ${item.color}">${item.value}</div>
          </div>
        `).join('')}
      </div>

      <!-- Gap Metrics -->
      <div class="gap-metrics">
        ${[
          { label: "Salary Due → Paid", value: gapMetrics.avgSalaryDueToPaid !== null ? `${gapMetrics.avgSalaryDueToPaid} days` : "N/A" },
          { label: "Salary Paid → Bill Gen", value: gapMetrics.avgSalaryPaidToBillGen !== null ? `${gapMetrics.avgSalaryPaidToBillGen} days` : "N/A" },
          { label: "Bill Gen → Submission", value: gapMetrics.avgBillGenToSubmission !== null ? `${gapMetrics.avgBillGenToSubmission} days` : "N/A" }
        ].map(item => `
          <div class="metric-card">
            <div class="metric-label">${item.label}</div>
            <div class="metric-value">${item.value}</div>
          </div>
        `).join('')}
      </div>

      <!-- Table -->
      <div class="table-container">
        <table>
          <thead>
            <tr>
              ${headers.map(header => `<th>${header}</th>`).join('')}
            </tr>
          </thead>
          <tbody>
            ${filteredSalaries.map(salary => {
              const monthNames = ["January", "February", "March", "April", "May", "June", "July", "August", "September", "October", "November", "December"];
              const monthIndex = monthNames.indexOf(salary?.month);
              let salaryDueDate = "N/A";
              if (monthIndex !== -1 && salary?.year) {
                const lastDate = new Date(parseInt(salary.year), monthIndex + 1, 0);
                salaryDueDate = lastDate.toISOString().split('T')[0];
              }
              
              return `
                <tr>
                  <td>${salary?.invoice_no || "N/A"}</td>
                  <td>${salary?.month || "N/A"}</td>
                  <td>${salary?.year || "N/A"}</td>
                  <td>${salary?.state_name || "N/A"}</td>
                  <td>${salary?.district || "N/A"}</td>
                  <td>${salary?.vc_name || "N/A"}</td>
                  <td>${salaryDueDate}</td>
                  <td>${salary?.salaryPaidDate ? new Date(salary.salaryPaidDate).toISOString().split('T')[0] : "N/A"}</td>
                  <td class="text-right">₹${(salary?.salaryDue || 0).toLocaleString()}</td>
                  <td class="text-right">₹${(salary?.additionalDueAmount || 0).toLocaleString()}</td>
                  <td class="text-right">₹${(salary?.total_due_amount || 0).toLocaleString()}</td>
                  <td class="text-right">₹${(salary?.salaryPaid || 0).toLocaleString()}</td>
                  <td class="text-right">₹${(salary?.additionalPaidAmount || 0).toLocaleString()}</td>
                  <td class="text-right">₹${(salary?.total_paid_amount || 0).toLocaleString()}</td>
                  <td class="text-right">₹${(salary?.tds_deducted || 0).toLocaleString()}</td>
                  <td class="text-right">₹${(salary?.adjustments || 0).toLocaleString()}</td>
                  <td class="text-right">₹${(salary?.total_salary_paid || 0).toLocaleString()}</td>
                  <td class="text-right">₹${(salary?.unpaid_salary || 0).toLocaleString()}</td>
                  <td>${salary?.created_by_details?.email || " "}</td>
                  <td>${salary?.remarks || "N/A"}</td>
                </tr>
              `;
            }).join('')}
          </tbody>
        </table>
      </div>

      <!-- Footer -->
      <div class="footer">
        <span>Generated on ${new Date().toLocaleString()}</span>
        <span>${filteredSalaries.length} records • Total: ₹${filteredSummary.totalSalaryDue.toLocaleString()}</span>
      </div>
    </body>
    </html>
  `;

  // Open print window
  const printWindow = window.open('', '_blank', 'width=1200,height=800');
  if (printWindow) {
    printWindow.document.write(printContent);
    printWindow.document.close();
    printWindow.focus();
    printWindow.print();
  } else {
    toast.error('Please allow pop-ups for printing', { autoClose: 3000 });
  }
};

  const headers = [
    "Select",
    "Invoice Number",
    "Month",
    "Year",
    "State",
    "District",
    "vc_name",
    "Salary Due Date",
    "Salary Paid Date",
    "Salary Due (₹)",
    "Additional Due Amount (₹)",
    "Total Due Amount (₹)",
    "Salary Paid (₹)",
    "Additional Paid Amount (₹)",
    "Total Paid Amount (₹)",
    "Deductions (₹)",
    "Adjustments (₹)",
    "Total Salary Paid (₹)",
    "Unpaid Salary (₹)",
    "Created by",
    "Remarks",
    "Actions",
  ];

  const rows = Array.isArray(filteredSalaries)
    ? filteredSalaries
        .filter((salary) => 
          !invoiceSearch || 
          (salary.invoice_no || "").toLowerCase() === invoiceSearch.toLowerCase().trim()
        )
        .map((salary) => {
        const isEditing = editingRow === salary._id;

        return {
          id: salary?._id,
          data: [
            <Checkbox
              checked={selectedSalaryIds.includes(salary._id)}
              onChange={() => handleSelectSalary(salary._id)}
              size="small"
            />,
            isEditing ? (
              <input
                type="text"
                value={editableData.invoiceNumber || ""}
                onChange={(e) => handleEditableChange(e, "invoiceNumber")}
                style={{
                  border: "1px solid #D1D5DB",
                  borderRadius: "4px",
                  padding: "4px",
                  width: "100%",
                  boxShadow: "0 1px 2px rgba(0, 0, 0, 0.1)",
                }}
              />
            ) : (
              salary?.invoice_no || "N/A"
            ),
            salary?.month || "N/A",
            salary?.year || "N/A",
            salary?.state_name || "N/A",
            salary?.district || "N/A",
            salary?.vc_name || "N/A",
            // Salary Due Date - last date of the month
            (() => {
              const monthNames = ["January", "February", "March", "April", "May", "June", "July", "August", "September", "October", "November", "December"];
              const monthIndex = monthNames.indexOf(salary?.month);
              if (monthIndex === -1 || !salary?.year) return "N/A";
              const year = parseInt(salary.year);
              const lastDate = new Date(year, monthIndex + 1, 0);
              // Format as YYYY-MM-DD in local timezone
              const yyyy = lastDate.getFullYear();
              const mm = String(lastDate.getMonth() + 1).padStart(2, '0');
              const dd = String(lastDate.getDate()).padStart(2, '0');
              return `${yyyy}-${mm}-${dd}`;
            })(),
            // Salary Paid Date - editable or display
            isEditing ? (
              <input
                type="date"
                value={editableData.salaryPaidDate || ""}
                onChange={(e) => handleEditableChange(e, "salaryPaidDate")}
                style={{
                  border: "1px solid #D1D5DB",
                  borderRadius: "4px",
                  padding: "4px",
                  width: "100%",
                  boxShadow: "0 1px 2px rgba(0, 0, 0, 0.1)",
                }}
              />
            ) : (
              salary?.salaryPaidDate ? new Date(salary.salaryPaidDate).toISOString().split('T')[0] : "N/A"
            ),
            `₹${(salary?.salaryDue || 0).toLocaleString()}`,
            `₹${(salary?.additionalDueAmount || 0).toLocaleString()}`,
            `₹${(salary?.total_due_amount || 0).toLocaleString()}`,
            isEditing ? (
              <input
                type="number"
                value={editableData.salaryPaid || ""}
                onChange={(e) => handleEditableChange(e, "salaryPaid")}
                style={{
                  border: "1px solid #D1D5DB",
                  borderRadius: "4px",
                  padding: "4px",
                  width: "100%",
                  boxShadow: "0 1px 2px rgba(0, 0, 0, 0.1)",
                }}
              />
            ) : (
              `₹${(salary?.salaryPaid || 0).toLocaleString()}`
            ),
            isEditing ? (
              <input
                type="number"
                value={editableData.additionalPaidAmount || ""}
                onChange={(e) =>
                  handleEditableChange(e, "additionalPaidAmount")
                }
                style={{
                  border: "1px solid #D1D5DB",
                  borderRadius: "4px",
                  padding: "4px",
                  width: "100%",
                  boxShadow: "0 1px 2px rgba(0, 0, 0, 0.1)",
                }}
              />
            ) : (
              `₹${(salary?.additionalPaidAmount || 0).toLocaleString()}`
            ),
            `₹${(salary?.total_paid_amount || 0).toLocaleString()}`,
            isEditing ? (
              <input
                type="number"
                value={editableData.tdsDeducted || ""}
                onChange={(e) => handleEditableChange(e, "tdsDeducted")}
                style={{
                  border: "1px solid #D1D5DB",
                  borderRadius: "4px",
                  padding: "4px",
                  width: "100%",
                  boxShadow: "0 1px 2px rgba(0, 0, 0, 0.1)",
                }}
              />
            ) : (
              `₹${(salary?.tds_deducted || 0).toLocaleString()}`
            ),
            isEditing ? (
              <input
                type="number"
                value={editableData.adjustments || ""}
                onChange={(e) => handleEditableChange(e, "adjustments")}
                style={{
                  border: "1px solid #D1D5DB",
                  borderRadius: "4px",
                  padding: "4px",
                  width: "100%",
                  boxShadow: "0 1px 2px rgba(0, 0, 0, 0.1)",
                }}
              />
            ) : (
              `₹${(salary?.adjustments || 0).toLocaleString()}`
            ),
            `₹${(salary?.total_salary_paid || 0).toLocaleString()}`,
            `₹${(salary?.unpaid_salary || 0).toLocaleString()}`,
            salary?.created_by_details?.email || salary?.created_by_details?.name || "N/A",

            isEditing ? (
              <input
                type="text"
                value={editableData.remarks || ""}
                onChange={(e) => handleEditableChange(e, "remarks")}
                placeholder="Add remarks"
                style={{
                  border: "1px solid #D1D5DB",
                  borderRadius: "4px",
                  padding: "4px",
                  width: "100%",
                  boxShadow: "0 1px 2px rgba(0, 0, 0, 0.1)",
                }}
              />
            ) : (
              salary?.remarks || "N/A"
            ),
            <div style={{ display: "flex", gap: "8px" }}>
              <Button
                variant="contained"
                color="info"
                onClick={() => handleViewRow(salary)}
                style={{ minWidth: "60px", padding: "4px 8px", fontSize: "12px" }}
              >
                View
              </Button>
             
              {canEditSalary() && (
                <>
                  {isEditing ? (
                    <Button
                      variant="contained"
                      color="primary"
                      onClick={() => handleSaveRow(salary._id)}
                    >
                      Save
                    </Button>
                  ) : (
                    <Button
                      variant="contained"
                      color="secondary"
                      onClick={() => handleEdit(salary)}
                    >
                      Edit
                    </Button>
                  )}
                </>
              )}
              {canDeleteSalary() && (
                <Button
                  variant="contained"
                  color="error"
                  onClick={() => handleDeleteRow(salary._id)}
                >
                  Delete
                </Button>
              )}
            </div>,
          ],
        };
      })
    : [];

  const paginatedRows = rows.slice(
    page * rowsPerPage,
    page * rowsPerPage + rowsPerPage
  );

  const handleChangePage = (event, newPage) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  if (error) return <div>Error: {error.message}</div>;

  const formFields = [
    { label: "Month", name: "month" },
    { label: "Year", name: "year" },
    { label: "State", name: "state" },
    { label: "District", name: "district" },
    { label: "VC Name", name: "vc" },
    { label: "Salary Due (₹)", name: "salaryDue" },
    { label: "Additional Due Amount (₹)", name: "additionalDueAmount" },
  ];

  // Styles
  const containerStyle = {
    backgroundColor: "#eaedf2",
    padding: isMobile ? "12px" : "24px",
    minHeight: "100vh",
    width: "100%",
    overflowX: "hidden",
    fontFamily: "Poppins, sans-serif",
  };

  const wrapperStyle = {
    maxWidth: isMobile ? "100%" : "1200px",
    margin: "0 auto",
    width: "100%",
  };

  const summaryGridStyle = {
    display: "grid",
    gridTemplateColumns: isMobile ? "1fr" : "repeat(5, 1fr)",
    gap: "14px",
    marginBottom: "24px",
  };



// Hover effect styles (to be added in your CSS or styled-components)
const hoverStyles = {
  transform: "translateY(-4px) scale(1.02)",
  boxShadow: "0 8px 25px rgba(0, 0, 0, 0.2)",
};

  const chartSectionStyle = {
    backgroundColor: "#ffffff",
    padding: isMobile ? "12px" : "24px",
    borderRadius: "8px",
    boxShadow: "0 2px 4px rgba(0, 0, 0, 0.1)",
    marginBottom: "24px",
  };

  const filtersPanelStyle = {
    display: "flex",
    gap: "16px",
    marginBottom: "14px",
    padding: "6px",
    backgroundColor: "#ffffff",
    borderRadius: "8px",
    boxShadow: "0 2px 4px rgba(0, 0, 0, 0.1)",
    flexWrap: "wrap",
  };

  const tableSectionStyle = {
    backgroundColor: "#ffffff",
    padding: isMobile ? "12px" : "24px",
    borderRadius: "8px",
    boxShadow: "0 2px 4px rgba(0, 0, 0, 0.1)",
  };

  const importSectionStyle = {
    backgroundColor: "#ffffff",
    padding: isMobile ? "12px" : "24px",
    borderRadius: "8px",
    boxShadow: "0 2px 4px rgba(0, 0, 0, 0.1)",
    marginTop: "24px",
  };

  const modalStyle = {
    position: "fixed",
    top: "0",
    left: "0",
    width: "100vw",
    height: "100vh",
    backgroundColor: "rgba(0, 0, 0, 0.5)",
    display: "flex",
    justifyContent: "center",
    alignItems: "center",
    zIndex: "1000",
  };

  const modalContentStyle = {
    backgroundColor: "#ffffff",
    padding: "14px",
    borderRadius: "12px",
    width: isMobile ? "95%" : "90%",
    maxWidth: isMobile ? "none" : "400px",
    maxHeight: "90vh",
    overflowY: "auto",
    boxShadow: "0 4px 6px rgba(0, 0, 0, 0.1)",
  };

  const inputStyle = {
    width: "100%",
    padding: "10px",
    border: "1px solid #d1d5db",
    borderRadius: "6px",
    boxShadow: "0 1px 3px rgba(0, 0, 0, 0.1)",
    fontSize: "16px",
    boxSizing: "border-box",
  };

  const buttonStyle = {
    backgroundColor: "#10b981",
    color: "#ffffff",
    borderRadius: "6px",
    padding: isMobile ? "6px 12px" : "8px 16px",
    fontSize: isMobile ? "12px" : "16px",
    border: "none",
    cursor: "pointer",
    boxSizing: "border-box",
  };

  return (
    <div style={containerStyle}>
      <div style={wrapperStyle}>
        
        {/* Summary Section */}
<div style={summaryGridStyle}>
  {[
    {
      label: "Total Salary Due (₹)",
      value: `₹${filteredSummary.totalSalaryDue.toLocaleString()}`,
      color: "#3b82f6",
      icon: "💰"
    },
    {
      label: "Total Salary Paid (₹)",
      value: `₹${filteredSummary.totalPaidSalary.toLocaleString()}`,
      color: "#10b981",
      icon: "✅"
    },
    {
      label: "Deductions (₹)",
      value: `₹${filteredSummary.totalDeductions.toLocaleString()}`,
      color: "#8b5cf6",
      icon: "📊"
    },
    {
      label: "Total Adjustments (₹)",
      value: `₹${filteredSummary.totalAdjustments.toLocaleString()}`,
      color: "#f59e0b",
      icon: "🔄"
    },
    {
      label: "Total Unpaid Salary (₹)",
      value: `₹${filteredSummary.totalUnpaidSalary.toLocaleString()}`,
      color: "#ef4444",
      icon: "⚠️"
    },
  ].map((item) => {
    const isHovered = hoveredCard === item.label;
    
    return (
      <div
        key={item.label}
        style={{
          ...cardStyle,
          backgroundColor: item.color,
          transform: isHovered ? "translateY(-6px) scale(1.02)" : "translateY(0) scale(1)",
          boxShadow: isHovered 
            ? "0 12px 35px rgba(0, 0, 0, 0.25)" 
            : "0 2px 8px rgba(0, 0, 0, 0.08)",
          borderColor: isHovered ? "rgba(255, 255, 255, 0.4)" : "rgba(255, 255, 255, 0.15)",
        }}
        onMouseEnter={() => setHoveredCard(item.label)}
        onMouseLeave={() => setHoveredCard(null)}
      >
        {/* Background gradient overlay on hover */}
        <div style={{
          position: "absolute",
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          background: isHovered 
            ? "linear-gradient(135deg, rgba(255,255,255,0.15) 0%, transparent 60%)" 
            : "linear-gradient(135deg, rgba(255,255,255,0.05) 0%, transparent 50%)",
          transition: "all 0.3s ease",
          pointerEvents: "none",
        }} />
        
        {/* Icon */}
        <div style={{
          fontSize: "28px",
          marginBottom: "4px",
          position: "relative",
          zIndex: 1,
          transform: isHovered ? "scale(1.1)" : "scale(1)",
          transition: "transform 0.3s ease",
        }}>
          {item.icon}
        </div>
        
        {/* Label */}
        <h6 style={{
          margin: 0,
          fontSize: isMobile ? "13px" : "15px",
          fontWeight: "500",
          opacity: 0.9,
          position: "relative",
          zIndex: 1,
          letterSpacing: "0.3px",
        }}>
          {item.label}
        </h6>
        
        {/* Value */}
        <p style={{
          fontSize: isMobile ? "18px" : "24px",
          margin: "6px 0 0",
          fontWeight: "700",
          position: "relative",
          zIndex: 1,
          textShadow: "0 1px 3px rgba(0,0,0,0.1)",
          letterSpacing: "0.5px",
        }}>
          {item.value}
        </p>
        
        {/* Bottom accent line on hover */}
        <div style={{
          position: "absolute",
          bottom: 0,
          left: isHovered ? "10%" : "50%",
          right: isHovered ? "10%" : "50%",
          height: "3px",
          backgroundColor: "rgba(255,255,255,0.4)",
          borderRadius: "2px",
          transition: "all 0.3s ease",
          opacity: isHovered ? 1 : 0,
        }} />
      </div>
    );
  })}
</div>

        {/* Average Gap Metrics Section */}
        <div style={{
          backgroundColor: "#ffffff",
          padding: isMobile ? "16px" : "24px",
          borderRadius: "8px",
          boxShadow: "0 2px 4px rgba(0, 0, 0, 0.1)",
          marginBottom: "24px",
        }}>
          <div style={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            marginBottom: "16px",
          }}>
            <h2 style={{
              fontWeight: "600",
              fontSize: isMobile ? "16px" : "20px",
              margin: 0,
              color: "#2d3748",
            }}>
              Average Gap Metrics
            </h2>
            <button
              onClick={() => {
                console.log('🔄 Manual refresh triggered');
                setRefreshTrigger(prev => prev + 1); // Increment to trigger useEffect
              }}
              style={{
                backgroundColor: "#3b82f6",
                color: "#ffffff",
                border: "none",
                borderRadius: "6px",
                padding: "8px 16px",
                fontSize: "14px",
                cursor: "pointer",
                display: "flex",
                alignItems: "center",
                gap: "6px",
                transition: "background-color 0.2s",
              }}
              onMouseEnter={(e) => e.target.style.backgroundColor = "#2563eb"}
              onMouseLeave={(e) => e.target.style.backgroundColor = "#3b82f6"}
              disabled={gapMetricsLoading}
            >
              <span style={{ fontSize: "16px" }}>🔄</span>
              {gapMetricsLoading ? "Refreshing..." : "Refresh"}
            </button>
          </div>
          <div style={{
            display: "grid",
            gridTemplateColumns: isMobile ? "1fr" : "repeat(3, 1fr)",
            gap: "20px",
          }}>
            {[
              {
                label: "Salary Due → Paid",
                value: gapMetrics.avgSalaryDueToPaid !== null ? `${gapMetrics.avgSalaryDueToPaid} days` : "N/A",
                color: gapMetrics.avgSalaryDueToPaid === null ? "#9ca3af" : gapMetrics.avgSalaryDueToPaid <= 7 ? "#10b981" : gapMetrics.avgSalaryDueToPaid <= 15 ? "#f59e0b" : "#ef4444",
                icon: "📅",
              },
              {
                label: "Salary Paid → Bill Gen",
                value: gapMetrics.avgSalaryPaidToBillGen !== null ? `${gapMetrics.avgSalaryPaidToBillGen} days` : "N/A",
                color: gapMetrics.avgSalaryPaidToBillGen === null ? "#9ca3af" : gapMetrics.avgSalaryPaidToBillGen <= 7 ? "#10b981" : gapMetrics.avgSalaryPaidToBillGen <= 15 ? "#f59e0b" : "#ef4444",
                icon: "📄",
              },
              {
                label: "Bill Gen → Submission",
                value: gapMetrics.avgBillGenToSubmission !== null ? `${gapMetrics.avgBillGenToSubmission} days` : "N/A",
                color: gapMetrics.avgBillGenToSubmission === null ? "#9ca3af" : gapMetrics.avgBillGenToSubmission <= 7 ? "#10b981" : gapMetrics.avgBillGenToSubmission <= 15 ? "#f59e0b" : "#ef4444",
                icon: "📤",
              },
            ].map((item) => (
              <div
                key={item.label}
                style={{
                  backgroundColor: item.color,
                  color: "#ffffff",
                  padding: "16px",
                  borderRadius: "8px",
                  boxShadow: "0 2px 4px rgba(0, 0, 0, 0.1)",
                  textAlign: "center",
                  position: "relative",
                  minHeight: "120px",
                  display: "flex",
                  flexDirection: "column",
                  justifyContent: "center",
                  alignItems: "center",
                }}
              >
                {gapMetricsLoading ? (
                  // Loading spinner
                  <>
                    <div style={{
                      width: "40px",
                      height: "40px",
                      border: "4px solid rgba(255, 255, 255, 0.3)",
                      borderTop: "4px solid #ffffff",
                      borderRadius: "50%",
                      animation: "spin 1s linear infinite",
                    }} />
                    <style>
                      {`
                        @keyframes spin {
                          0% { transform: rotate(0deg); }
                          100% { transform: rotate(360deg); }
                        }
                      `}
                    </style>
                    <p style={{ fontSize: "12px", marginTop: "12px", opacity: 0.9 }}>
                      Loading...
                    </p>
                  </>
                ) : (
                  // Actual data
                  <>
                    <div style={{ fontSize: "24px", marginBottom: "8px" }}>
                      {item.icon}
                    </div>
                    <h6 style={{ margin: 0, fontSize: isMobile ? "12px" : "14px", fontWeight: "500" }}>
                      {item.label}
                    </h6>
                    <p style={{ fontSize: isMobile ? "16px" : "20px", margin: "8px 0 0", fontWeight: "bold" }}>
                      {item.value}
                    </p>
                  </>
                )}
              </div>
            ))}
          </div>
          <div style={{
            marginTop: "16px",
            padding: "12px",
            backgroundColor: "#f3f4f6",
            borderRadius: "6px",
            fontSize: "12px",
            color: "#6b7280",
          }}>
            <strong>Note:</strong> Color coding - 🟢 Green: 0-7 days (Good) | 🟡 Yellow: 8-15 days (Moderate) | 🔴 Red: 15+ days (Delayed)
          </div>
        </div>

        {/* Chart Section */}
      <div style={{
  background: "linear-gradient(135deg, #ffffff 0%, #fafbfc 100%)",
  padding: isMobile ? "16px" : "24px",
  borderRadius: "16px",
  border: "1px solid #eef2f6",
  marginBottom: "24px",
  boxShadow: "0 1px 3px rgba(0, 0, 0, 0.04)",
  transition: "box-shadow 0.3s ease",
  position: "relative",
  overflow: "hidden",
}}
onMouseEnter={(e) => {
  e.currentTarget.style.boxShadow = "0 8px 30px rgba(0, 0, 0, 0.06)";
}}
onMouseLeave={(e) => {
  e.currentTarget.style.boxShadow = "0 1px 3px rgba(0, 0, 0, 0.04)";
}}
>
  {/* Decorative Elements */}
  <div style={{
    position: "absolute",
    top: "-60px",
    right: "-40px",
    width: "180px",
    height: "180px",
    borderRadius: "50%",
    background: "radial-gradient(circle, rgba(74, 144, 217, 0.04) 0%, transparent 70%)",
    pointerEvents: "none",
  }} />
  <div style={{
    position: "absolute",
    bottom: "-40px",
    left: "-30px",
    width: "120px",
    height: "120px",
    borderRadius: "50%",
    background: "radial-gradient(circle, rgba(16, 185, 129, 0.03) 0%, transparent 70%)",
    pointerEvents: "none",
  }} />

  {/* Header with Icon */}
  <div style={{
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    marginBottom: "20px",
    flexWrap: isMobile ? "wrap" : "nowrap",
    gap: isMobile ? "12px" : "0",
    position: "relative",
    zIndex: 1,
  }}>
    <div style={{
      display: "flex",
      alignItems: "center",
      gap: "12px",
    }}>
      <div style={{
        width: "40px",
        height: "40px",
        borderRadius: "10px",
        background: "linear-gradient(135deg, #4a90d9 0%, #357abd 100%)",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        fontSize: "18px",
        color: "#fff",
        flexShrink: 0,
        boxShadow: "0 4px 12px rgba(74, 144, 217, 0.25)",
      }}>
        📊
      </div>
      <div>
        <h2 style={{
          fontWeight: 700,
          fontSize: isMobile ? "17px" : "20px",
          margin: 0,
          color: "#1a2332",
          letterSpacing: "-0.02em",
        }}>
          Salary Distribution
        </h2>
        <p style={{
          margin: "2px 0 0 0",
          fontSize: "12px",
          color: "#64748b",
        }}>
          Overview of salary metrics
        </p>
      </div>
    </div>
    
    {/* Legend / Summary Badge */}
    <div style={{
      display: "flex",
      gap: "16px",
      alignItems: "center",
      flexWrap: "wrap",
    }}>
      <div style={{
        display: "flex",
        alignItems: "center",
        gap: "6px",
        padding: "4px 12px",
        borderRadius: "20px",
        background: "#f1f5f9",
        border: "1px solid #e2e8f0",
      }}>
        <span style={{
          width: "10px",
          height: "10px",
          borderRadius: "3px",
          background: "#4a90d9",
        }} />
        <span style={{
          fontSize: "11px",
          color: "#475569",
          fontWeight: 500,
        }}>
          Due: ₹{filteredSummary.totalSalaryDue.toLocaleString()}
        </span>
      </div>
      <div style={{
        display: "flex",
        alignItems: "center",
        gap: "6px",
        padding: "4px 12px",
        borderRadius: "20px",
        background: "#f1f5f9",
        border: "1px solid #e2e8f0",
      }}>
        <span style={{
          width: "10px",
          height: "10px",
          borderRadius: "3px",
          background: "#48bb78",
        }} />
        <span style={{
          fontSize: "11px",
          color: "#475569",
          fontWeight: 500,
        }}>
          Paid: ₹{filteredSummary.totalPaidSalary.toLocaleString()}
        </span>
      </div>
    </div>
  </div>

  {/* Chart Container */}
  <div style={{
    background: "#ffffff",
    borderRadius: "12px",
    padding: "16px",
    border: "1px solid #f0f0f0",
    position: "relative",
    zIndex: 1,
  }}>
    <div style={{
      height: isMobile ? "200px" : "350px",
      position: "relative",
    }}>
      <canvas ref={chartRef} id="salaryChart"></canvas>
    </div>
  </div>

 
 

 
</div>





        <div 
          style={{
            background: "#ffffff",
            padding: isMobile ? "16px" : "20px 24px",
            borderRadius: "12px",
            border: "1px solid #eef2f6",
            marginBottom: "24px",
            boxShadow: "0 1px 3px rgba(0, 0, 0, 0.04)",
            transition: "box-shadow 0.3s ease",
          }}
          onMouseEnter={(e) => e.currentTarget.style.boxShadow = "0 4px 12px rgba(0, 0, 0, 0.06)"}
          onMouseLeave={(e) => e.currentTarget.style.boxShadow = "0 1px 3px rgba(0, 0, 0, 0.04)"}
        >
  
  {/* Filter Title */}
  <div style={{
    display: "flex",
    alignItems: "center",
    gap: "8px",
    marginBottom: "16px",
    paddingBottom: "12px",
    borderBottom: "1px solid #f0f0f0",
  }}>
    <span style={{ fontSize: "16px" }}>🔍</span>
    <span style={{
      fontSize: "14px",
      fontWeight: 600,
      color: "#1a2332",
      letterSpacing: "0.02em",
    }}>Filters</span>
    <span style={{
      fontSize: "11px",
      color: "#94a3b8",
      fontWeight: 400,
      marginLeft: "4px",
    }}>
      {filteredSalaries.length > 0 ? `(${filteredSalaries.length} records)` : ""}
    </span>
  </div>

  {/* First Row - Dropdowns */}
  <div style={{
    display: "grid",
    gridTemplateColumns: isMobile ? "1fr 1fr" : "repeat(5, 1fr)",
    gap: "12px",
    marginBottom: "14px",
  }}>
    <Select
      value={filters.month || ""}
      onChange={(e) => handleFilterChange("month", e.target.value)}
      displayEmpty
      style={{ 
        width: "100%",
        height: "42px",
        fontSize: "13px",
        backgroundColor: "#ffffff",
        borderRadius: "8px",
      }}
      sx={{
        '& .MuiSelect-select': {
          padding: '8px 14px',
          fontSize: '13px',
          borderRadius: '8px',
        },
        '& .MuiOutlinedInput-notchedOutline': {
          borderColor: '#d1d9e6',
          borderRadius: '8px',
        },
        '&:hover .MuiOutlinedInput-notchedOutline': {
          borderColor: '#4a90d9',
        },
        '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
          borderColor: '#4a90d9',
          borderWidth: '2px',
        },
      }}
    >
      <MenuItem value="" style={{ fontSize: "13px" }}>📅 Month</MenuItem>
      {uniqueMonths.map((month) => (
        <MenuItem key={month} value={month} style={{ fontSize: "13px" }}>
          {month}
        </MenuItem>
      ))}
    </Select>

    <Select
      value={filters.year || ""}
      onChange={(e) => handleFilterChange("year", e.target.value)}
      displayEmpty
      style={{ 
        width: "100%",
        height: "42px",
        fontSize: "13px",
        backgroundColor: "#ffffff",
        borderRadius: "8px",
      }}
      sx={{
        '& .MuiSelect-select': {
          padding: '8px 14px',
          fontSize: '13px',
          borderRadius: '8px',
        },
        '& .MuiOutlinedInput-notchedOutline': {
          borderColor: '#d1d9e6',
          borderRadius: '8px',
        },
        '&:hover .MuiOutlinedInput-notchedOutline': {
          borderColor: '#4a90d9',
        },
        '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
          borderColor: '#4a90d9',
          borderWidth: '2px',
        },
      }}
    >
      <MenuItem value="" style={{ fontSize: "13px" }}>📆 Year</MenuItem>
      {uniqueYears.map((year) => (
        <MenuItem key={year} value={year} style={{ fontSize: "13px" }}>
          {year}
        </MenuItem>
      ))}
    </Select>

    <Select
      value={filters.state || ""}
      onChange={(e) => handleFilterChange("state", e.target.value)}
      displayEmpty
      style={{ 
        width: "100%",
        height: "42px",
        fontSize: "13px",
        backgroundColor: "#ffffff",
        borderRadius: "8px",
      }}
      sx={{
        '& .MuiSelect-select': {
          padding: '8px 14px',
          fontSize: '13px',
          borderRadius: '8px',
        },
        '& .MuiOutlinedInput-notchedOutline': {
          borderColor: '#d1d9e6',
          borderRadius: '8px',
        },
        '&:hover .MuiOutlinedInput-notchedOutline': {
          borderColor: '#4a90d9',
        },
        '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
          borderColor: '#4a90d9',
          borderWidth: '2px',
        },
      }}
    >
      <MenuItem value="" style={{ fontSize: "13px" }}>📍 State</MenuItem>
      {uniqueStates.map((state) => (
        <MenuItem key={state} value={state} style={{ fontSize: "13px" }}>
          {state}
        </MenuItem>
      ))}
    </Select>

    <Select
      value={filters.district || ""}
      onChange={(e) => handleFilterChange("district", e.target.value)}
      displayEmpty
      style={{ 
        width: "100%",
        height: "42px",
        fontSize: "13px",
        backgroundColor: "#ffffff",
        borderRadius: "8px",
      }}
      sx={{
        '& .MuiSelect-select': {
          padding: '8px 14px',
          fontSize: '13px',
          borderRadius: '8px',
        },
        '& .MuiOutlinedInput-notchedOutline': {
          borderColor: '#d1d9e6',
          borderRadius: '8px',
        },
        '&:hover .MuiOutlinedInput-notchedOutline': {
          borderColor: '#4a90d9',
        },
        '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
          borderColor: '#4a90d9',
          borderWidth: '2px',
        },
      }}
    >
      <MenuItem value="" style={{ fontSize: "13px" }}>🏙️ District</MenuItem>
      {uniqueDistricts.map((district) => (
        <MenuItem key={district} value={district} style={{ fontSize: "13px" }}>
          {district}
        </MenuItem>
      ))}
    </Select>

    <Select
      value={filters.vc || ""}
      onChange={(e) => handleFilterChange("vc", e.target.value)}
      displayEmpty
      style={{ 
        width: "100%",
        height: "42px",
        fontSize: "13px",
        backgroundColor: "#ffffff",
        borderRadius: "8px",
      }}
      sx={{
        '& .MuiSelect-select': {
          padding: '8px 14px',
          fontSize: '13px',
          borderRadius: '8px',
        },
        '& .MuiOutlinedInput-notchedOutline': {
          borderColor: '#d1d9e6',
          borderRadius: '8px',
        },
        '&:hover .MuiOutlinedInput-notchedOutline': {
          borderColor: '#4a90d9',
        },
        '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
          borderColor: '#4a90d9',
          borderWidth: '2px',
        },
      }}
    >
      <MenuItem value="" style={{ fontSize: "13px" }}>👤 VC Name</MenuItem>
      {uniqueVCs.map((vc) => (
        <MenuItem key={vc} value={vc} style={{ fontSize: "13px" }}>
          {vc}
        </MenuItem>
      ))}
    </Select>
  </div>
  
  {/* Second Row - Date inputs and Buttons */}
  <div style={{
    display: "grid",
    gridTemplateColumns: isMobile ? "1fr 1fr" : "1fr 1fr auto auto",
    gap: "12px",
    alignItems: "center",
  }}>
    <div style={{ position: "relative" }}>
      <span style={{
        position: "absolute",
        left: "12px",
        top: "50%",
        transform: "translateY(-50%)",
        color: "#94a3b8",
        fontSize: "14px",
        pointerEvents: "none",
      }}>📅</span>
      <input
        type="date"
        value={filters.fromDate || ""}
        onChange={(e) => handleFilterChange("fromDate", e.target.value)}
        placeholder="From Date"
        style={{
          padding: "8px 14px 8px 38px",
          border: "1px solid #d1d9e6",
          borderRadius: "8px",
          fontSize: "13px",
          height: "42px",
          width: "100%",
          boxSizing: "border-box",
          backgroundColor: "#ffffff",
          transition: "border-color 0.2s ease, box-shadow 0.2s ease",
          outline: "none",
          color: "#1a2332",
          cursor: "pointer",
        }}
        onFocus={(e) => {
          e.target.style.borderColor = "#4a90d9";
          e.target.style.boxShadow = "0 0 0 3px rgba(74, 144, 217, 0.1)";
        }}
        onBlur={(e) => {
          e.target.style.borderColor = "#d1d9e6";
          e.target.style.boxShadow = "none";
        }}
      />
    </div>

    <div style={{ position: "relative" }}>
      <span style={{
        position: "absolute",
        left: "12px",
        top: "50%",
        transform: "translateY(-50%)",
        color: "#94a3b8",
        fontSize: "14px",
        pointerEvents: "none",
      }}>📅</span>
      <input
        type="date"
        value={filters.toDate || ""}
        onChange={(e) => handleFilterChange("toDate", e.target.value)}
        placeholder="To Date"
        style={{
          padding: "8px 14px 8px 38px",
          border: "1px solid #d1d9e6",
          borderRadius: "8px",
          fontSize: "13px",
          height: "42px",
          width: "100%",
          boxSizing: "border-box",
          backgroundColor: "#ffffff",
          transition: "border-color 0.2s ease, box-shadow 0.2s ease",
          outline: "none",
          color: "#1a2332",
          cursor: "pointer",
        }}
        onFocus={(e) => {
          e.target.style.borderColor = "#4a90d9";
          e.target.style.boxShadow = "0 0 0 3px rgba(74, 144, 217, 0.1)";
        }}
        onBlur={(e) => {
          e.target.style.borderColor = "#d1d9e6";
          e.target.style.boxShadow = "none";
        }}
      />
    </div>

    <button
      onClick={applyFilters}
      style={{
        background: "linear-gradient(135deg, #10b981 0%, #059669 100%)",
        color: "#fff",
        border: "none",
        borderRadius: "8px",
        padding: "0 28px",
        fontSize: "13px",
        fontWeight: 600,
        height: "42px",
        whiteSpace: "nowrap",
        cursor: "pointer",
        transition: "all 0.25s ease",
        boxShadow: "0 2px 8px rgba(16, 185, 129, 0.3)",
        gridColumn: isMobile ? "span 1" : "auto",
        letterSpacing: "0.3px",
        display: "inline-flex",
        alignItems: "center",
        gap: "6px",
      }}
      onMouseEnter={(e) => {
        e.target.style.transform = "translateY(-2px)";
        e.target.style.boxShadow = "0 4px 14px rgba(16, 185, 129, 0.4)";
        e.target.style.background = "linear-gradient(135deg, #059669 0%, #047857 100%)";
      }}
      onMouseLeave={(e) => {
        e.target.style.transform = "translateY(0)";
        e.target.style.boxShadow = "0 2px 8px rgba(16, 185, 129, 0.3)";
        e.target.style.background = "linear-gradient(135deg, #10b981 0%, #059669 100%)";
      }}
    >
      <span>✓</span> Apply
    </button>

    <button
      onClick={resetFilters}
      style={{
        background: "#ffffff",
        color: "#ef4444",
        border: "1.5px solid #ef4444",
        borderRadius: "8px",
        padding: "0 28px",
        fontSize: "13px",
        fontWeight: 600,
        height: "42px",
        whiteSpace: "nowrap",
        cursor: "pointer",
        transition: "all 0.25s ease",
        gridColumn: isMobile ? "span 1" : "auto",
        letterSpacing: "0.3px",
        display: "inline-flex",
        alignItems: "center",
        gap: "6px",
      }}
      onMouseEnter={(e) => {
        e.target.style.background = "#ef4444";
        e.target.style.color = "#fff";
        e.target.style.transform = "translateY(-2px)";
        e.target.style.boxShadow = "0 4px 12px rgba(239, 68, 68, 0.2)";
      }}
      onMouseLeave={(e) => {
        e.target.style.background = "#ffffff";
        e.target.style.color = "#ef4444";
        e.target.style.transform = "translateY(0)";
        e.target.style.boxShadow = "none";
      }}
    >
      <span>⟳</span> Reset
    </button>
  </div>

  {/* Active Filters Display */}
  {Object.values(filters).some(v => v) && (
    <div style={{
      display: "flex",
      flexWrap: "wrap",
      gap: "6px",
      marginTop: "14px",
      paddingTop: "14px",
      borderTop: "1px solid #f0f0f0",
    }}>
      <span style={{
        fontSize: "12px",
        color: "#64748b",
        fontWeight: 500,
        marginRight: "4px",
      }}>Active Filters:</span>
      {Object.entries(filters).map(([key, value]) => {
        if (!value) return null;
        const label = {
          month: "Month",
          year: "Year",
          state: "State",
          district: "District",
          vc: "VC",
          fromDate: "From",
          toDate: "To"
        }[key] || key;
        return (
          <span key={key} style={{
            background: "#f1f5f9",
            color: "#1e293b",
            padding: "2px 10px",
            borderRadius: "12px",
            fontSize: "11px",
            fontWeight: 500,
            display: "inline-flex",
            alignItems: "center",
            gap: "4px",
          }}>
            {label}: {value}
          </span>
        );
      })}
    </div>
  )}
</div>

        {/* Table Section */}
      <div style={tableSectionStyle}>
  <div style={{
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
    marginBottom: "20px",
    flexWrap: isMobile ? "wrap" : "nowrap",
    gap: isMobile ? "12px" : "0",
  }}>
    <h2 style={{
      fontWeight: "600",
      fontSize: isMobile ? "18px" : "22px",
      margin: 0,
      color: "#1a2332",
      letterSpacing: "-0.01em",
    }}>
      Salary Records
    </h2>
   
  </div>

{/* Search and Actions Bar - All in One Row */}
<div style={{
  display: "flex",
  gap: "12px",
  alignItems: "center",
  justifyContent: "space-between",
  marginBottom: "16px",
  padding: "12px 16px",
  background: "#f8fafc",
  borderRadius: "8px",
  border: "1px solid #eef2f6",
  flexWrap: isMobile ? "wrap" : "nowrap",
}}>
  
  {/* Left Side - Search */}
  <div style={{
    display: "flex",
    gap: "8px",
    alignItems: "center",
    flex: "1",
    minWidth: isMobile ? "100%" : "auto",
  }}>
    <div style={{
      position: "relative",
      flex: "1",
      maxWidth: isMobile ? "100%" : "380px",
    }}>
      <span style={{
        position: "absolute",
        left: "12px",
        top: "50%",
        transform: "translateY(-50%)",
        color: "#94a3b8",
        fontSize: "14px",
      }}>🔍</span>
      <input
        type="text"
        placeholder="Search by Invoice Number..."
        value={invoiceSearchInput}
        onChange={(e) => setInvoiceSearchInput(e.target.value)}
        onKeyPress={(e) => {
          if (e.key === 'Enter') {
            setInvoiceSearch(invoiceSearchInput.trim());
          }
        }}
        style={{
          width: "100%",
          padding: "8px 12px 8px 36px",
          border: "1px solid #d1d9e6",
          borderRadius: "6px",
          fontSize: "14px",
          boxSizing: "border-box",
          background: "#ffffff",
          transition: "border-color 0.2s, box-shadow 0.2s",
          outline: "none",
        }}
        onFocus={(e) => {
          e.target.style.borderColor = "#4a90d9";
          e.target.style.boxShadow = "0 0 0 3px rgba(74, 144, 217, 0.1)";
        }}
        onBlur={(e) => {
          e.target.style.borderColor = "#d1d9e6";
          e.target.style.boxShadow = "none";
        }}
      />
    </div>
    <button
      onClick={() => setInvoiceSearch(invoiceSearchInput.trim())}
      style={{
        background: "#4a90d9",
        color: "#fff",
        border: "none",
        borderRadius: "6px",
        padding: "8px 18px",
        fontSize: "13px",
        fontWeight: "500",
        cursor: "pointer",
        transition: "all 0.2s ease",
        whiteSpace: "nowrap",
      }}
      onMouseEnter={(e) => e.target.style.background = "#357abd"}
      onMouseLeave={(e) => e.target.style.background = "#4a90d9"}
    >
      Search
    </button>
    {invoiceSearch && (
      <button
        onClick={() => {
          setInvoiceSearch("");
          setInvoiceSearchInput("");
        }}
        style={{
          background: "transparent",
          color: "#ef4444",
          border: "1px solid #ef4444",
          borderRadius: "6px",
          padding: "7px 16px",
          fontSize: "13px",
          fontWeight: "500",
          cursor: "pointer",
          transition: "all 0.2s ease",
          whiteSpace: "nowrap",
        }}
        onMouseEnter={(e) => {
          e.target.style.background = "#ef4444";
          e.target.style.color = "#fff";
        }}
        onMouseLeave={(e) => {
          e.target.style.background = "transparent";
          e.target.style.color = "#ef4444";
        }}
      >
        ✕ Clear
      </button>
    )}
  </div>

  {/* Right Side - Action Buttons */}
  <div style={{
    display: "flex",
    gap: "8px",
    alignItems: "center",
    flexShrink: 0,
    flexWrap: isMobile ? "wrap" : "nowrap",
  }}>
    {/* Delete Selected Button - Only for Super Admin */}
    {user?.role === "superadmin" && selectedSalaryIds.length > 0 && (
      <button
        onClick={() => handleBulkDelete('selected')}
        style={{
          background: "#ef4444",
          color: "#fff",
          border: "none",
          borderRadius: "6px",
          padding: "8px 16px",
          fontSize: "13px",
          fontWeight: "500",
          cursor: "pointer",
          display: "inline-flex",
          alignItems: "center",
          gap: "6px",
          transition: "all 0.2s ease",
          whiteSpace: "nowrap",
        }}
        onMouseEnter={(e) => e.target.style.background = "#dc2626"}
        onMouseLeave={(e) => e.target.style.background = "#ef4444"}
      >
        <span>🗑️</span>
        Delete ({selectedSalaryIds.length})
      </button>
    )}

    {/* Add Salary Record Button */}
    {hasAdminPrivileges() && (
      <button
        onClick={() => setIsModalOpen(true)}
        style={{
          background: "linear-gradient(135deg, #4a90d9 0%, #357abd 100%)",
          color: "#fff",
          border: "none",
          borderRadius: "6px",
          padding: "8px 18px",
          fontSize: "13px",
          fontWeight: "500",
          cursor: "pointer",
          display: "inline-flex",
          alignItems: "center",
          gap: "6px",
          transition: "all 0.2s ease",
          boxShadow: "0 2px 8px rgba(74, 144, 217, 0.25)",
          whiteSpace: "nowrap",
        }}
        onMouseEnter={(e) => {
          e.target.style.transform = "translateY(-1px)";
          e.target.style.boxShadow = "0 4px 12px rgba(74, 144, 217, 0.35)";
        }}
        onMouseLeave={(e) => {
          e.target.style.transform = "translateY(0)";
          e.target.style.boxShadow = "0 2px 8px rgba(74, 144, 217, 0.25)";
        }}
      >
        <span style={{ fontSize: "16px", lineHeight: 1 }}>+</span>
        Add Record
      </button>
    )}
  </div>
</div>
  
  

  {/* Table */}
  <TableContainer
    component={Paper}
    style={{
      maxWidth: "100%",
      overflowX: "auto",
      overflowY: "auto",
      maxHeight: "1200px",
      minHeight: "600px",
      marginTop: "16px",
      borderRadius: "10px",
      border: "1px solid #eef2f6",
      boxShadow: "0 1px 3px rgba(0, 0, 0, 0.04)",
    }}
  >
    <Table
      style={{ minWidth: 650 }}
      aria-label="salary table"
      stickyHeader
    >
      <TableHead>
        <TableRow>
          {headers.map((header, index) => (
            <TableCell
              key={header}
              className={index === headers.length - 1 ? "sticky-action-cell" : ""}
              style={{
                fontWeight: 600,
                fontSize: "12px",
                padding: "12px 16px",
                cursor: index !== 0 && index !== headers.length - 1 ? "pointer" : "default",
                backgroundColor: "#f8fafc",
                borderBottom: "2px solid #eef2f6",
                position: "sticky",
                top: 0,
                zIndex: index === 0 ? 3 : index === headers.length - 1 ? 2 : 1,
                color: "#475569",
                textTransform: "uppercase",
                letterSpacing: "0.03em",
                whiteSpace: "nowrap",
                right: index === headers.length - 1 ? 0 : "auto",
                boxShadow: index === headers.length - 1 ? "-2px 0 5px rgba(0,0,0,0.02)" : "none",
                ...(index === headers.length - 1 && { width: "240px", minWidth: "240px" }),
              }}
              onClick={() =>
                index !== 0 && index !== headers.length - 1 &&
                handleSort(
                  index === 1 ? "invoice_no" :
                  index === 2 ? "month" :
                  index === 3 ? "year" :
                  index === 4 ? "state" :
                  index === 5 ? "district" :
                  index === 6 ? "vc_name" :
                  index === 7 ? "salaryDueDate" :
                  index === 8 ? "salaryPaidDate" :
                  index === 9 ? "salaryDue" :
                  index === 10 ? "additionalDueAmount" :
                  index === 11 ? "total_due_amount" :
                  index === 12 ? "salaryPaid" :
                  index === 13 ? "additionalPaidAmount" :
                  index === 14 ? "total_paid_amount" :
                  index === 15 ? "tds_deducted" :
                  index === 16 ? "adjustments" :
                  index === 17 ? "total_salary_paid" :
                  index === 18 ? "unpaid_salary" :
                  index === 19 ? "created_by" : "remarks",
                  index
                )
              }
              onMouseEnter={(e) => {
                if (index !== 0 && index !== headers.length - 1) {
                  e.target.style.color = "#1a2332";
                }
              }}
              onMouseLeave={(e) => {
                if (index !== 0 && index !== headers.length - 1) {
                  e.target.style.color = "#475569";
                }
              }}
            >
              <div style={{ display: "flex", alignItems: "center", gap: "4px" }}>
                {index === 0 ? (
                  <Checkbox
                    checked={paginatedRows.length > 0 && paginatedRows.every(row => selectedSalaryIds.includes(row.id))}
                    indeterminate={paginatedRows.some(row => selectedSalaryIds.includes(row.id)) && !paginatedRows.every(row => selectedSalaryIds.includes(row.id))}
                    onChange={handleSelectAllSalaries}
                    size="small"
                  />
                ) : (
                  <>
                    {header}
                    {sortConfig.key === (
                      index === 1 ? "invoice_no" :
                      index === 2 ? "month" :
                      index === 3 ? "year" :
                      index === 4 ? "state" :
                      index === 5 ? "district" :
                      index === 6 ? "vc_name" :
                      index === 7 ? "salaryDueDate" :
                      index === 8 ? "salaryPaidDate" :
                      index === 9 ? "salaryDue" :
                      index === 10 ? "additionalDueAmount" :
                      index === 11 ? "total_due_amount" :
                      index === 12 ? "salaryPaid" :
                      index === 13 ? "additionalPaidAmount" :
                      index === 14 ? "total_paid_amount" :
                      index === 15 ? "tds_deducted" :
                      index === 16 ? "adjustments" :
                      index === 17 ? "total_salary_paid" :
                      index === 18 ? "unpaid_salary" :
                      index === 19 ? "created_by" : "remarks"
                    ) && (
                      <span style={{ fontSize: "10px", color: "#4a90d9" }}>
                        {sortConfig.direction === "asc" ? "↑" : "↓"}
                      </span>
                    )}
                  </>
                )}
              </div>
            </TableCell>
          ))}
        </TableRow>
      </TableHead>
      <TableBody>
        {isLoading ? (
          <TableRow>
            <TableCell colSpan={headers.length} align="center" style={{ padding: "40px 0", color: "#94a3b8" }}>
              <div style={{ display: "flex", flexDirection: "column", alignItems: "center", gap: "8px" }}>
                <div style={{
                  width: "32px",
                  height: "32px",
                  border: "3px solid #eef2f6",
                  borderTop: "3px solid #4a90d9",
                  borderRadius: "50%",
                  animation: "spin 0.8s linear infinite",
                }} />
                <span style={{ fontSize: "14px" }}>Loading...</span>
              </div>
            </TableCell>
          </TableRow>
        ) : paginatedRows.length === 0 ? (
          <TableRow>
            <TableCell colSpan={headers.length} align="center" style={{ padding: "60px 0", color: "#94a3b8" }}>
              <div style={{ display: "flex", flexDirection: "column", alignItems: "center", gap: "8px" }}>
                <span style={{ fontSize: "40px" }}>📋</span>
                <span style={{ fontSize: "14px", fontWeight: 500 }}>No data available</span>
                <span style={{ fontSize: "12px" }}>Try adjusting your filters or search criteria</span>
              </div>
            </TableCell>
          </TableRow>
        ) : (
          paginatedRows.map((row, rowIndex) => (
            <TableRow
              key={rowIndex}
              style={{
                transition: "background-color 0.15s ease",
              }}
              onMouseEnter={(e) => {
                e.currentTarget.style.backgroundColor = "#f8fafc";
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.backgroundColor = "transparent";
              }}
            >
              {row.data.map((data, index) => (
                <TableCell
                  key={index}
                  className={index === row.data.length - 1 ? "sticky-action-cell" : ""}
                  style={{
                    padding: "10px 16px",
                    fontSize: "13px",
                    borderBottom: "1px solid #f0f0f0",
                    color: index >= 9 && index <= 18 ? "#1a2332" : "#475569",
                    fontWeight: index >= 9 && index <= 18 ? 500 : 400,
                    ...(index !== row.data.length - 1 ? {
                      maxWidth: "200px",
                      overflow: "hidden",
                      textOverflow: "ellipsis",
                      whiteSpace: "nowrap",
                    } : {
                      whiteSpace: "nowrap",
                      width: "240px",
                      minWidth: "240px",
                    }),
                    position: index === row.data.length - 1 ? "sticky" : "static",
                    right: index === row.data.length - 1 ? 0 : "auto",
                    backgroundColor: index === row.data.length - 1 ? "#ffffff" : "transparent",
                    boxShadow: index === row.data.length - 1 ? "-2px 0 5px rgba(0,0,0,0.02)" : "none",
                    zIndex: index === row.data.length - 1 ? 1 : "auto",
                  }}
                >
                  {data}
                </TableCell>
              ))}
            </TableRow>
          ))
        )}
      </TableBody>
    </Table>
    
    {/* Pagination */}
    <div style={{
      display: "flex",
      justifyContent: "space-between",
      alignItems: "center",
      padding: "12px 16px",
      borderTop: "1px solid #eef2f6",
      background: "#fafbfc",
      borderRadius: "0 0 10px 10px",
      flexWrap: isMobile ? "wrap" : "nowrap",
      gap: isMobile ? "8px" : "0",
    }}>
      <div style={{ display: "flex", alignItems: "center", gap: "8px" }}>
        <span style={{ fontSize: "13px", color: "#64748b" }}>Rows per page:</span>
        <select
          value={rowsPerPage}
          onChange={handleChangeRowsPerPage}
          style={{
            padding: "4px 8px",
            border: "1px solid #d1d9e6",
            borderRadius: "4px",
            fontSize: "13px",
            background: "#fff",
            outline: "none",
            cursor: "pointer",
          }}
        >
          {[10, 25, 50, 100].map((option) => (
            <option key={option} value={option}>{option}</option>
          ))}
        </select>
      </div>
      <div style={{ display: "flex", alignItems: "center", gap: "12px" }}>
        <span style={{ fontSize: "13px", color: "#64748b" }}>
          {page * rowsPerPage + 1}-
          {Math.min((page + 1) * rowsPerPage, rows.length)} of {rows.length}
        </span>
        <div style={{ display: "flex", gap: "4px" }}>
          <button
            onClick={() => handleChangePage(null, 0)}
            disabled={page === 0}
            style={{
              padding: "4px 8px",
              border: "1px solid #d1d9e6",
              borderRadius: "4px",
              background: "#fff",
              cursor: page === 0 ? "not-allowed" : "pointer",
              opacity: page === 0 ? 0.4 : 1,
              color: "#475569",
              fontSize: "12px",
              transition: "all 0.2s",
            }}
            onMouseEnter={(e) => {
              if (page !== 0) {
                e.target.style.background = "#f1f5f9";
              }
            }}
            onMouseLeave={(e) => {
              e.target.style.background = "#fff";
            }}
          >
            ⟪
          </button>
          <button
            onClick={() => handleChangePage(null, page - 1)}
            disabled={page === 0}
            style={{
              padding: "4px 8px",
              border: "1px solid #d1d9e6",
              borderRadius: "4px",
              background: "#fff",
              cursor: page === 0 ? "not-allowed" : "pointer",
              opacity: page === 0 ? 0.4 : 1,
              color: "#475569",
              fontSize: "12px",
              transition: "all 0.2s",
            }}
            onMouseEnter={(e) => {
              if (page !== 0) {
                e.target.style.background = "#f1f5f9";
              }
            }}
            onMouseLeave={(e) => {
              e.target.style.background = "#fff";
            }}
          >
            ⟨
          </button>
          <button
            onClick={() => handleChangePage(null, page + 1)}
            disabled={page >= Math.ceil(rows.length / rowsPerPage) - 1}
            style={{
              padding: "4px 8px",
              border: "1px solid #d1d9e6",
              borderRadius: "4px",
              background: "#fff",
              cursor: page >= Math.ceil(rows.length / rowsPerPage) - 1 ? "not-allowed" : "pointer",
              opacity: page >= Math.ceil(rows.length / rowsPerPage) - 1 ? 0.4 : 1,
              color: "#475569",
              fontSize: "12px",
              transition: "all 0.2s",
            }}
            onMouseEnter={(e) => {
              if (page < Math.ceil(rows.length / rowsPerPage) - 1) {
                e.target.style.background = "#f1f5f9";
              }
            }}
            onMouseLeave={(e) => {
              e.target.style.background = "#fff";
            }}
          >
            ⟩
          </button>
          <button
            onClick={() => handleChangePage(null, Math.ceil(rows.length / rowsPerPage) - 1)}
            disabled={page >= Math.ceil(rows.length / rowsPerPage) - 1}
            style={{
              padding: "4px 8px",
              border: "1px solid #d1d9e6",
              borderRadius: "4px",
              background: "#fff",
              cursor: page >= Math.ceil(rows.length / rowsPerPage) - 1 ? "not-allowed" : "pointer",
              opacity: page >= Math.ceil(rows.length / rowsPerPage) - 1 ? 0.4 : 1,
              color: "#475569",
              fontSize: "12px",
              transition: "all 0.2s",
            }}
            onMouseEnter={(e) => {
              if (page < Math.ceil(rows.length / rowsPerPage) - 1) {
                e.target.style.background = "#f1f5f9";
              }
            }}
            onMouseLeave={(e) => {
              e.target.style.background = "#fff";
            }}
          >
            ⟫
          </button>
        </div>
      </div>
    </div>
  </TableContainer>

  {/* Add loading animation */}
  <style>
    {`
      @keyframes spin {
        0% { transform: rotate(0deg); }
        100% { transform: rotate(360deg); }
      }
    `}
  </style>
</div>


{/* Export Data Card - Modern Premium Design */}
<div style={{
  background: "linear-gradient(145deg, #ffffff 0%, #fafbfc 100%)",
  padding: isMobile ? "20px" : "28px",
  marginTop: "20px",
  borderRadius: "20px",
  border: "1px solid rgba(238, 242, 246, 0.8)",
  marginBottom: "24px",
  boxShadow: "0 4px 20px rgba(0, 0, 0, 0.02), 0 1px 3px rgba(0, 0, 0, 0.03)",
  position: "relative",
  overflow: "hidden",
  backdropFilter: "blur(10px)",
  transition: "all 0.4s cubic-bezier(0.4, 0, 0.2, 1)",
}}
onMouseEnter={(e) => {
  e.currentTarget.style.boxShadow = "0 12px 48px rgba(0, 0, 0, 0.06), 0 1px 3px rgba(0, 0, 0, 0.04)";
  e.currentTarget.style.transform = "translateY(-2px)";
  e.currentTarget.style.borderColor = "#d1d9e6";
}}
onMouseLeave={(e) => {
  e.currentTarget.style.boxShadow = "0 4px 20px rgba(0, 0, 0, 0.02), 0 1px 3px rgba(0, 0, 0, 0.03)";
  e.currentTarget.style.transform = "translateY(0)";
  e.currentTarget.style.borderColor = "rgba(238, 242, 246, 0.8)";
}}
>
  {/* Animated Gradient Background */}
  <div style={{
    position: "absolute",
    top: "-50%",
    right: "-30%",
    width: "60%",
    height: "100%",
    background: "radial-gradient(circle, rgba(74, 144, 217, 0.03) 0%, transparent 70%)",
    pointerEvents: "none",
    animation: "float 6s ease-in-out infinite",
  }} />
  <div style={{
    position: "absolute",
    bottom: "-40%",
    left: "-20%",
    width: "50%",
    height: "80%",
    background: "radial-gradient(circle, rgba(16, 185, 129, 0.02) 0%, transparent 70%)",
    pointerEvents: "none",
    animation: "float 8s ease-in-out infinite reverse",
  }} />

  {/* Header with Animated Icon */}
  <div style={{
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    marginBottom: "24px",
    paddingBottom: "20px",
    borderBottom: "1px solid rgba(238, 242, 246, 0.6)",
    position: "relative",
    zIndex: 1,
    flexWrap: isMobile ? "wrap" : "nowrap",
    gap: isMobile ? "12px" : "0",
  }}>
    <div style={{
      display: "flex",
      alignItems: "center",
      gap: "14px",
    }}>
      <div style={{
        width: "52px",
        height: "52px",
        borderRadius: "16px",
        background: "linear-gradient(135deg, #4a90d9 0%, #357abd 100%)",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        fontSize: "22px",
        color: "#fff",
        flexShrink: 0,
        boxShadow: "0 8px 24px rgba(74, 144, 217, 0.25)",
        position: "relative",
        animation: "pulse-glow 3s ease-in-out infinite",
      }}>
        💾
        <div style={{
          position: "absolute",
          top: "-4px",
          right: "-4px",
          width: "16px",
          height: "16px",
          borderRadius: "50%",
          background: "#10b981",
          border: "2px solid #fff",
          animation: "blink 2s ease-in-out infinite",
        }} />
      </div>
      <div>
        <h3 style={{
          fontWeight: 700,
          fontSize: isMobile ? "17px" : "20px",
          margin: 0,
          color: "#1a2332",
          letterSpacing: "-0.02em",
          background: "linear-gradient(135deg, #1a2332 0%, #4a90d9 100%)",
          WebkitBackgroundClip: "text",
          WebkitTextFillColor: "transparent",
        }}>
          Export Data
        </h3>
        <p style={{
          margin: "2px 0 0 0",
          fontSize: "13px",
          color: "#64748b",
          display: "flex",
          alignItems: "center",
          gap: "6px",
        }}>
          <span>📋</span>
          {filteredSalaries.length} records · 
          <span>💰</span>
          ₹{filteredSummary.totalSalaryDue.toLocaleString()}
        </p>
      </div>
    </div>
    
    {/* Quick Stats Badge */}
    <div style={{
      display: "flex",
      gap: "8px",
      alignItems: "center",
      padding: "6px 16px",
      borderRadius: "100px",
      background: "rgba(255, 255, 255, 0.8)",
      backdropFilter: "blur(10px)",
      border: "1px solid rgba(238, 242, 246, 0.6)",
      boxShadow: "0 2px 8px rgba(0, 0, 0, 0.02)",
    }}>
      <span style={{
        width: "8px",
        height: "8px",
        borderRadius: "50%",
        background: "#10b981",
        display: "inline-block",
        animation: "pulse 2s ease-in-out infinite",
      }} />
      <span style={{
        fontSize: "12px",
        color: "#475569",
        fontWeight: 500,
      }}>
        Ready to export
      </span>
    </div>
  </div>

  {/* Export Options - Modern Card Grid */}
  <div style={{
    display: "grid",
    gridTemplateColumns: isMobile ? "1fr 1fr" : "repeat(4, 1fr)",
    gap: "16px",
    position: "relative",
    zIndex: 1,
  }}>
    
    {/* CSV Export Card */}
    <div style={{
      background: "rgba(255, 255, 255, 0.9)",
      backdropFilter: "blur(10px)",
      borderRadius: "16px",
      padding: "20px 16px",
      border: "1px solid rgba(238, 242, 246, 0.6)",
      textAlign: "center",
      transition: "all 0.4s cubic-bezier(0.34, 1.56, 0.64, 1)",
      cursor: "pointer",
      boxShadow: "0 2px 8px rgba(0, 0, 0, 0.02)",
      position: "relative",
      overflow: "hidden",
    }}
    onMouseEnter={(e) => {
      e.currentTarget.style.transform = "translateY(-8px) scale(1.02)";
      e.currentTarget.style.boxShadow = "0 16px 40px rgba(74, 144, 217, 0.15)";
      e.currentTarget.style.borderColor = "#4a90d9";
      e.currentTarget.style.background = "#ffffff";
    }}
    onMouseLeave={(e) => {
      e.currentTarget.style.transform = "translateY(0) scale(1)";
      e.currentTarget.style.boxShadow = "0 2px 8px rgba(0, 0, 0, 0.02)";
      e.currentTarget.style.borderColor = "rgba(238, 242, 246, 0.6)";
      e.currentTarget.style.background = "rgba(255, 255, 255, 0.9)";
    }}
    onClick={exportAsCSV}
    >
      {/* Shine Effect */}
      <div style={{
        position: "absolute",
        top: 0,
        left: 0,
        right: 0,
        height: "2px",
        background: "linear-gradient(90deg, transparent, #4a90d9, transparent)",
        opacity: 0,
        transition: "opacity 0.4s ease",
      }}
      onMouseEnter={(e) => e.currentTarget.style.opacity = "1"}
      onMouseLeave={(e) => e.currentTarget.style.opacity = "0"}
      />
      
      <div style={{
        width: "56px",
        height: "56px",
        borderRadius: "16px",
        background: "linear-gradient(135deg, #eff6ff 0%, #dbeafe 100%)",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        margin: "0 auto 12px",
        fontSize: "24px",
        transition: "all 0.4s cubic-bezier(0.34, 1.56, 0.64, 1)",
        position: "relative",
      }}
      onMouseEnter={(e) => {
        e.currentTarget.style.transform = "scale(1.15) rotate(-5deg)";
        e.currentTarget.style.boxShadow = "0 8px 24px rgba(74, 144, 217, 0.2)";
      }}
      onMouseLeave={(e) => {
        e.currentTarget.style.transform = "scale(1) rotate(0deg)";
        e.currentTarget.style.boxShadow = "none";
      }}
      >
        📄
      </div>
      
      <h4 style={{
        fontSize: "14px",
        fontWeight: 700,
        color: "#1a2332",
        margin: "0 0 4px 0",
        transition: "color 0.3s ease",
      }}
      onMouseEnter={(e) => e.currentTarget.style.color = "#4a90d9"}
      onMouseLeave={(e) => e.currentTarget.style.color = "#1a2332"}
      >
        CSV
      </h4>
      
      <p style={{
        fontSize: "12px",
        color: "#94a3b8",
        margin: 0,
        lineHeight: 1.4,
      }}>
        Spreadsheet format
      </p>
      
      <div style={{
        marginTop: "14px",
        padding: "6px 16px",
        borderRadius: "100px",
        background: "rgba(74, 144, 217, 0.08)",
        fontSize: "11px",
        color: "#4a90d9",
        fontWeight: 600,
        display: "inline-block",
        transition: "all 0.3s ease",
      }}
      onMouseEnter={(e) => {
        e.currentTarget.style.background = "#4a90d9";
        e.currentTarget.style.color = "#fff";
        e.currentTarget.style.transform = "scale(1.05)";
      }}
      onMouseLeave={(e) => {
        e.currentTarget.style.background = "rgba(74, 144, 217, 0.08)";
        e.currentTarget.style.color = "#4a90d9";
        e.currentTarget.style.transform = "scale(1)";
      }}
      >
        ⬇ Download
      </div>
    </div>

    {/* PDF Export Card */}
    <div style={{
      background: "rgba(255, 255, 255, 0.9)",
      backdropFilter: "blur(10px)",
      borderRadius: "16px",
      padding: "20px 16px",
      border: "1px solid rgba(238, 242, 246, 0.6)",
      textAlign: "center",
      transition: "all 0.4s cubic-bezier(0.34, 1.56, 0.64, 1)",
      cursor: "pointer",
      boxShadow: "0 2px 8px rgba(0, 0, 0, 0.02)",
      position: "relative",
      overflow: "hidden",
    }}
    onMouseEnter={(e) => {
      e.currentTarget.style.transform = "translateY(-8px) scale(1.02)";
      e.currentTarget.style.boxShadow = "0 16px 40px rgba(239, 68, 68, 0.12)";
      e.currentTarget.style.borderColor = "#ef4444";
      e.currentTarget.style.background = "#ffffff";
    }}
    onMouseLeave={(e) => {
      e.currentTarget.style.transform = "translateY(0) scale(1)";
      e.currentTarget.style.boxShadow = "0 2px 8px rgba(0, 0, 0, 0.02)";
      e.currentTarget.style.borderColor = "rgba(238, 242, 246, 0.6)";
      e.currentTarget.style.background = "rgba(255, 255, 255, 0.9)";
    }}
    onClick={exportAsPDF}
    >
      <div style={{
        position: "absolute",
        top: 0,
        left: 0,
        right: 0,
        height: "2px",
        background: "linear-gradient(90deg, transparent, #ef4444, transparent)",
        opacity: 0,
        transition: "opacity 0.4s ease",
      }}
      onMouseEnter={(e) => e.currentTarget.style.opacity = "1"}
      onMouseLeave={(e) => e.currentTarget.style.opacity = "0"}
      />
      
      <div style={{
        width: "56px",
        height: "56px",
        borderRadius: "16px",
        background: "linear-gradient(135deg, #fef2f2 0%, #fecaca 100%)",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        margin: "0 auto 12px",
        fontSize: "24px",
        transition: "all 0.4s cubic-bezier(0.34, 1.56, 0.64, 1)",
      }}
      onMouseEnter={(e) => {
        e.currentTarget.style.transform = "scale(1.15) rotate(-5deg)";
        e.currentTarget.style.boxShadow = "0 8px 24px rgba(239, 68, 68, 0.2)";
      }}
      onMouseLeave={(e) => {
        e.currentTarget.style.transform = "scale(1) rotate(0deg)";
        e.currentTarget.style.boxShadow = "none";
      }}
      >
        📑
      </div>
      
      <h4 style={{
        fontSize: "14px",
        fontWeight: 700,
        color: "#1a2332",
        margin: "0 0 4px 0",
        transition: "color 0.3s ease",
      }}
      onMouseEnter={(e) => e.currentTarget.style.color = "#ef4444"}
      onMouseLeave={(e) => e.currentTarget.style.color = "#1a2332"}
      >
        PDF
      </h4>
      
      <p style={{
        fontSize: "12px",
        color: "#94a3b8",
        margin: 0,
        lineHeight: 1.4,
      }}>
        Document format
      </p>
      
      <div style={{
        marginTop: "14px",
        padding: "6px 16px",
        borderRadius: "100px",
        background: "rgba(239, 68, 68, 0.08)",
        fontSize: "11px",
        color: "#ef4444",
        fontWeight: 600,
        display: "inline-block",
        transition: "all 0.3s ease",
      }}
      onMouseEnter={(e) => {
        e.currentTarget.style.background = "#ef4444";
        e.currentTarget.style.color = "#fff";
        e.currentTarget.style.transform = "scale(1.05)";
      }}
      onMouseLeave={(e) => {
        e.currentTarget.style.background = "rgba(239, 68, 68, 0.08)";
        e.currentTarget.style.color = "#ef4444";
        e.currentTarget.style.transform = "scale(1)";
      }}
      >
        ⬇ Download
      </div>
    </div>

    {/* Excel Export Card */}
    <div style={{
      background: "rgba(255, 255, 255, 0.9)",
      backdropFilter: "blur(10px)",
      borderRadius: "16px",
      padding: "20px 16px",
      border: "1px solid rgba(238, 242, 246, 0.6)",
      textAlign: "center",
      transition: "all 0.4s cubic-bezier(0.34, 1.56, 0.64, 1)",
      cursor: "pointer",
      boxShadow: "0 2px 8px rgba(0, 0, 0, 0.02)",
      position: "relative",
      overflow: "hidden",
    }}
    onMouseEnter={(e) => {
      e.currentTarget.style.transform = "translateY(-8px) scale(1.02)";
      e.currentTarget.style.boxShadow = "0 16px 40px rgba(16, 185, 129, 0.12)";
      e.currentTarget.style.borderColor = "#10b981";
      e.currentTarget.style.background = "#ffffff";
    }}
    onMouseLeave={(e) => {
      e.currentTarget.style.transform = "translateY(0) scale(1)";
      e.currentTarget.style.boxShadow = "0 2px 8px rgba(0, 0, 0, 0.02)";
      e.currentTarget.style.borderColor = "rgba(238, 242, 246, 0.6)";
      e.currentTarget.style.background = "rgba(255, 255, 255, 0.9)";
    }}
    onClick={exportAsCSV}
    >
      <div style={{
        position: "absolute",
        top: 0,
        left: 0,
        right: 0,
        height: "2px",
        background: "linear-gradient(90deg, transparent, #10b981, transparent)",
        opacity: 0,
        transition: "opacity 0.4s ease",
      }}
      onMouseEnter={(e) => e.currentTarget.style.opacity = "1"}
      onMouseLeave={(e) => e.currentTarget.style.opacity = "0"}
      />
      
      <div style={{
        width: "56px",
        height: "56px",
        borderRadius: "16px",
        background: "linear-gradient(135deg, #f0fdf4 0%, #bbf7d0 100%)",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        margin: "0 auto 12px",
        fontSize: "24px",
        transition: "all 0.4s cubic-bezier(0.34, 1.56, 0.64, 1)",
      }}
      onMouseEnter={(e) => {
        e.currentTarget.style.transform = "scale(1.15) rotate(-5deg)";
        e.currentTarget.style.boxShadow = "0 8px 24px rgba(16, 185, 129, 0.2)";
      }}
      onMouseLeave={(e) => {
        e.currentTarget.style.transform = "scale(1) rotate(0deg)";
        e.currentTarget.style.boxShadow = "none";
      }}
      >
        📊
      </div>
      
      <h4 style={{
        fontSize: "14px",
        fontWeight: 700,
        color: "#1a2332",
        margin: "0 0 4px 0",
        transition: "color 0.3s ease",
      }}
      onMouseEnter={(e) => e.currentTarget.style.color = "#10b981"}
      onMouseLeave={(e) => e.currentTarget.style.color = "#1a2332"}
      >
        Excel
      </h4>
      
      <p style={{
        fontSize: "12px",
        color: "#94a3b8",
        margin: 0,
        lineHeight: 1.4,
      }}>
        Spreadsheet format
      </p>
      
      <div style={{
        marginTop: "14px",
        padding: "6px 16px",
        borderRadius: "100px",
        background: "rgba(16, 185, 129, 0.08)",
        fontSize: "11px",
        color: "#10b981",
        fontWeight: 600,
        display: "inline-block",
        transition: "all 0.3s ease",
      }}
      onMouseEnter={(e) => {
        e.currentTarget.style.background = "#10b981";
        e.currentTarget.style.color = "#fff";
        e.currentTarget.style.transform = "scale(1.05)";
      }}
      onMouseLeave={(e) => {
        e.currentTarget.style.background = "rgba(16, 185, 129, 0.08)";
        e.currentTarget.style.color = "#10b981";
        e.currentTarget.style.transform = "scale(1)";
      }}
      >
        ⬇ Download
      </div>
    </div>

    {/* Print Card */}
<div style={{
  background: "rgba(255, 255, 255, 0.9)",
  backdropFilter: "blur(10px)",
  borderRadius: "16px",
  padding: "20px 16px",
  border: "1px solid rgba(238, 242, 246, 0.6)",
  textAlign: "center",
  transition: "all 0.4s cubic-bezier(0.34, 1.56, 0.64, 1)",
  cursor: "pointer",
  boxShadow: "0 2px 8px rgba(0, 0, 0, 0.02)",
  position: "relative",
  overflow: "hidden",
}}
onMouseEnter={(e) => {
  e.currentTarget.style.transform = "translateY(-8px) scale(1.02)";
  e.currentTarget.style.boxShadow = "0 16px 40px rgba(139, 92, 246, 0.12)";
  e.currentTarget.style.borderColor = "#8b5cf6";
  e.currentTarget.style.background = "#ffffff";
}}
onMouseLeave={(e) => {
  e.currentTarget.style.transform = "translateY(0) scale(1)";
  e.currentTarget.style.boxShadow = "0 2px 8px rgba(0, 0, 0, 0.02)";
  e.currentTarget.style.borderColor = "rgba(238, 242, 246, 0.6)";
  e.currentTarget.style.background = "rgba(255, 255, 255, 0.9)";
}}
onClick={handlePrint}
>
  <div style={{
    position: "absolute",
    top: 0,
    left: 0,
    right: 0,
    height: "2px",
    background: "linear-gradient(90deg, transparent, #8b5cf6, transparent)",
    opacity: 0,
    transition: "opacity 0.4s ease",
  }}
  onMouseEnter={(e) => e.currentTarget.style.opacity = "1"}
  onMouseLeave={(e) => e.currentTarget.style.opacity = "0"}
  />
  
  <div style={{
    width: "56px",
    height: "56px",
    borderRadius: "16px",
    background: "linear-gradient(135deg, #f5f3ff 0%, #ede9fe 100%)",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    margin: "0 auto 12px",
    fontSize: "24px",
    transition: "all 0.4s cubic-bezier(0.34, 1.56, 0.64, 1)",
  }}
  onMouseEnter={(e) => {
    e.currentTarget.style.transform = "scale(1.15) rotate(-5deg)";
    e.currentTarget.style.boxShadow = "0 8px 24px rgba(139, 92, 246, 0.2)";
  }}
  onMouseLeave={(e) => {
    e.currentTarget.style.transform = "scale(1) rotate(0deg)";
    e.currentTarget.style.boxShadow = "none";
  }}
  >
    🖨️
  </div>
  
  <h4 style={{
    fontSize: "14px",
    fontWeight: 700,
    color: "#1a2332",
    margin: "0 0 4px 0",
    transition: "color 0.3s ease",
  }}
  onMouseEnter={(e) => e.currentTarget.style.color = "#8b5cf6"}
  onMouseLeave={(e) => e.currentTarget.style.color = "#1a2332"}
  >
    Print
  </h4>
  
  <p style={{
    fontSize: "12px",
    color: "#94a3b8",
    margin: 0,
    lineHeight: 1.4,
  }}>
    Direct printing
  </p>
  
  <div style={{
    marginTop: "14px",
    padding: "6px 16px",
    borderRadius: "100px",
    background: "rgba(139, 92, 246, 0.08)",
    fontSize: "11px",
    color: "#8b5cf6",
    fontWeight: 600,
    display: "inline-block",
    transition: "all 0.3s ease",
  }}
  onMouseEnter={(e) => {
    e.currentTarget.style.background = "#8b5cf6";
    e.currentTarget.style.color = "#fff";
    e.currentTarget.style.transform = "scale(1.05)";
  }}
  onMouseLeave={(e) => {
    e.currentTarget.style.background = "rgba(139, 92, 246, 0.08)";
    e.currentTarget.style.color = "#8b5cf6";
    e.currentTarget.style.transform = "scale(1)";
  }}
  >
    🖨 Print
  </div>
</div>
  </div>

  {/* Bottom Quick Actions */}
  <div style={{
    marginTop: "20px",
    paddingTop: "18px",
    borderTop: "1px solid rgba(238, 242, 246, 0.6)",
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
    flexWrap: isMobile ? "wrap" : "nowrap",
    gap: isMobile ? "10px" : "0",
    position: "relative",
    zIndex: 1,
  }}>
    <div style={{
      display: "flex",
      gap: "16px",
      alignItems: "center",
      flexWrap: "wrap",
    }}>
      <div style={{
        display: "flex",
        alignItems: "center",
        gap: "6px",
        padding: "4px 12px",
        borderRadius: "100px",
        background: "rgba(255, 255, 255, 0.6)",
        border: "1px solid rgba(238, 242, 246, 0.6)",
      }}>
        <span style={{ fontSize: "14px" }}>📋</span>
        <span style={{
          fontSize: "12px",
          color: "#475569",
          fontWeight: 500,
        }}>
          {filteredSalaries.length} records
        </span>
      </div>
      <div style={{
        display: "flex",
        alignItems: "center",
        gap: "6px",
        padding: "4px 12px",
        borderRadius: "100px",
        background: "rgba(255, 255, 255, 0.6)",
        border: "1px solid rgba(238, 242, 246, 0.6)",
      }}>
        <span style={{ fontSize: "14px" }}>💰</span>
        <span style={{
          fontSize: "12px",
          color: "#475569",
          fontWeight: 600,
        }}>
          ₹{filteredSummary.totalSalaryDue.toLocaleString()}
        </span>
      </div>
    </div>
    
    <div style={{
      fontSize: "11px",
      color: "#94a3b8",
      display: "flex",
      alignItems: "center",
      gap: "4px",
    }}>
      <span>⚡</span>
      Quick export options
    </div>
  </div>

  {/* Animations */}
  <style>
    {`
      @keyframes float {
        0%, 100% { transform: translateY(0px) rotate(0deg); }
        50% { transform: translateY(-10px) rotate(2deg); }
      }
      
      @keyframes pulse-glow {
        0%, 100% { box-shadow: 0 8px 24px rgba(74, 144, 217, 0.25); }
        50% { box-shadow: 0 8px 40px rgba(74, 144, 217, 0.35); }
      }
      
      @keyframes blink {
        0%, 100% { opacity: 1; transform: scale(1); }
        50% { opacity: 0.5; transform: scale(0.9); }
      }
      
      @keyframes pulse {
        0%, 100% { opacity: 1; transform: scale(1); }
        50% { opacity: 0.5; transform: scale(0.8); }
      }
    `}
  </style>
</div>


        {/* Bulk Import Section */}
        {/* Only show Bulk Import Salary section for Super Admin */}
       {hasAdminPrivileges() && (
  <div style={{
    background: "linear-gradient(135deg, #ffffff 0%, #f8fafc 100%)",
    padding: isMobile ? "20px" : "28px",
    borderRadius: "16px",
    border: "1px solid #eef2f6",
    marginTop: "24px",
    boxShadow: "0 1px 3px rgba(0, 0, 0, 0.04)",
    position: "relative",
    overflow: "hidden",
    transition: "all 0.3s ease",
  }}
  onMouseEnter={(e) => {
    e.currentTarget.style.boxShadow = "0 8px 30px rgba(0, 0, 0, 0.06)";
    e.currentTarget.style.borderColor = "#d1d9e6";
  }}
  onMouseLeave={(e) => {
    e.currentTarget.style.boxShadow = "0 1px 3px rgba(0, 0, 0, 0.04)";
    e.currentTarget.style.borderColor = "#eef2f6";
  }}
  >
    {/* Decorative Background Element */}
    <div style={{
      position: "absolute",
      top: "-50px",
      right: "-50px",
      width: "200px",
      height: "200px",
      borderRadius: "50%",
      background: "linear-gradient(135deg, rgba(74, 144, 217, 0.05) 0%, rgba(16, 185, 129, 0.05) 100%)",
      pointerEvents: "none",
    }} />

    <div style={{
      position: "absolute",
      bottom: "-80px",
      left: "-30px",
      width: "150px",
      height: "150px",
      borderRadius: "50%",
      background: "linear-gradient(135deg, rgba(16, 185, 129, 0.03) 0%, rgba(74, 144, 217, 0.03) 100%)",
      pointerEvents: "none",
    }} />

    {/* Header with Badge */}
    <div style={{
      display: "flex",
      alignItems: "center",
      justifyContent: "space-between",
      marginBottom: "24px",
      paddingBottom: "20px",
      borderBottom: "1px solid #f0f0f0",
      position: "relative",
      zIndex: 1,
      flexWrap: isMobile ? "wrap" : "nowrap",
      gap: isMobile ? "12px" : "0",
    }}>
      <div style={{
        display: "flex",
        alignItems: "center",
        gap: "14px",
      }}>
        <div style={{
          width: "48px",
          height: "48px",
          borderRadius: "12px",
          background: "linear-gradient(135deg, #4a90d9 0%, #357abd 100%)",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          fontSize: "20px",
          color: "#fff",
          flexShrink: 0,
          boxShadow: "0 4px 12px rgba(74, 144, 217, 0.3)",
        }}>
          📊
        </div>
        <div>
          <h2 style={{
            fontWeight: 700,
            fontSize: isMobile ? "18px" : "20px",
            margin: 0,
            color: "#1a2332",
            letterSpacing: "-0.02em",
          }}>
            Bulk Import
          </h2>
          <p style={{
            margin: "2px 0 0 0",
            fontSize: "13px",
            color: "#64748b",
          }}>
            Import multiple salary records in one go
          </p>
        </div>
      </div>
      
      {/* Status Badge */}
      <div style={{
        display: "flex",
        alignItems: "center",
        gap: "6px",
        padding: "6px 14px",
        borderRadius: "20px",
        background: selectedFile ? "#f0fdf4" : "#f1f5f9",
        border: `1px solid ${selectedFile ? "#bbf7d0" : "#e2e8f0"}`,
      }}>
        <span style={{
          width: "8px",
          height: "8px",
          borderRadius: "50%",
          background: selectedFile ? "#10b981" : "#94a3b8",
          animation: selectedFile ? "pulse 2s infinite" : "none",
        }} />
        <span style={{
          fontSize: "12px",
          fontWeight: 500,
          color: selectedFile ? "#166534" : "#64748b",
        }}>
          {selectedFile ? "File Ready" : "No File"}
        </span>
      </div>
    </div>

    {/* Main Content - Three Column Layout */}
    <div style={{
      display: "grid",
      gridTemplateColumns: isMobile ? "1fr" : "1fr 1.5fr 1fr",
      gap: "20px",
      position: "relative",
      zIndex: 1,
    }}>
      
      {/* Step 1: Download Template */}
      <div style={{
        background: "#ffffff",
        borderRadius: "12px",
        padding: "20px",
        border: "1px solid #eef2f6",
        textAlign: "center",
        transition: "all 0.3s ease",
        boxShadow: "0 2px 4px rgba(0, 0, 0, 0.02)",
      }}
      onMouseEnter={(e) => {
        e.currentTarget.style.transform = "translateY(-4px)";
        e.currentTarget.style.boxShadow = "0 8px 25px rgba(0, 0, 0, 0.06)";
        e.currentTarget.style.borderColor = "#d1d9e6";
      }}
      onMouseLeave={(e) => {
        e.currentTarget.style.transform = "translateY(0)";
        e.currentTarget.style.boxShadow = "0 2px 4px rgba(0, 0, 0, 0.02)";
        e.currentTarget.style.borderColor = "#eef2f6";
      }}
      >
        <div style={{
          width: "56px",
          height: "56px",
          borderRadius: "14px",
          background: "linear-gradient(135deg, #eff6ff 0%, #dbeafe 100%)",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          margin: "0 auto 12px",
          fontSize: "24px",
        }}>
          📄
        </div>
        <h3 style={{
          fontSize: "14px",
          fontWeight: 600,
          color: "#1a2332",
          margin: "0 0 4px 0",
        }}>
          Step 1: Download Template
        </h3>
        <p style={{
          fontSize: "12px",
          color: "#64748b",
          margin: "0 0 16px 0",
          lineHeight: 1.5,
        }}>
          Get the CSV template with correct headers
        </p>
        <button
          onClick={downloadCsv}
          style={{
            background: "linear-gradient(135deg, #4a90d9 0%, #357abd 100%)",
            color: "#fff",
            border: "none",
            borderRadius: "8px",
            padding: "10px 20px",
            fontSize: "13px",
            fontWeight: 500,
            cursor: "pointer",
            width: "100%",
            transition: "all 0.25s ease",
            boxShadow: "0 2px 8px rgba(74, 144, 217, 0.25)",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            gap: "8px",
          }}
          onMouseEnter={(e) => {
            e.target.style.transform = "translateY(-2px)";
            e.target.style.boxShadow = "0 4px 14px rgba(74, 144, 217, 0.35)";
          }}
          onMouseLeave={(e) => {
            e.target.style.transform = "translateY(0)";
            e.target.style.boxShadow = "0 2px 8px rgba(74, 144, 217, 0.25)";
          }}
        >
          <span>⬇️</span> Download Template
        </button>
      </div>

      {/* Step 2: Upload File */}
      <div style={{
        background: "#ffffff",
        borderRadius: "12px",
        padding: "20px",
        border: selectedFile ? "2px solid #10b981" : "2px dashed #d1d9e6",
        textAlign: "center",
        transition: "all 0.3s ease",
        cursor: "pointer",
        position: "relative",
        boxShadow: selectedFile ? "0 0 0 4px rgba(16, 185, 129, 0.1)" : "none",
      }}
      onMouseEnter={(e) => {
        if (!selectedFile) {
          e.currentTarget.style.borderColor = "#4a90d9";
          e.currentTarget.style.background = "#fafbfc";
        }
      }}
      onMouseLeave={(e) => {
        if (!selectedFile) {
          e.currentTarget.style.borderColor = "#d1d9e6";
          e.currentTarget.style.background = "#ffffff";
        }
      }}
      onDragOver={(e) => {
        e.preventDefault();
        e.currentTarget.style.borderColor = "#4a90d9";
        e.currentTarget.style.background = "#eff6ff";
      }}
      onDragLeave={(e) => {
        e.currentTarget.style.borderColor = selectedFile ? "#10b981" : "#d1d9e6";
        e.currentTarget.style.background = "#ffffff";
      }}
      onDrop={(e) => {
        e.preventDefault();
        const files = e.dataTransfer.files;
        if (files.length > 0) {
          const file = files[0];
          if (file.name.endsWith('.csv')) {
            const input = document.getElementById('file-upload-new');
            if (input) {
              const dt = new DataTransfer();
              dt.items.add(file);
              input.files = dt.files;
              input.dispatchEvent(new Event('change'));
            }
          }
        }
        e.currentTarget.style.borderColor = selectedFile ? "#10b981" : "#d1d9e6";
        e.currentTarget.style.background = "#ffffff";
      }}
      >
        <input
          id="file-upload-new"
          type="file"
          accept=".csv"
          onChange={handleFileChange}
          style={{
            position: "absolute",
            top: 0,
            left: 0,
            width: "100%",
            height: "100%",
            opacity: 0,
            cursor: "pointer",
          }}
        />
        
        <div style={{
          width: "56px",
          height: "56px",
          borderRadius: "14px",
          background: selectedFile 
            ? "linear-gradient(135deg, #f0fdf4 0%, #dcfce7 100%)" 
            : "linear-gradient(135deg, #f8fafc 0%, #f1f5f9 100%)",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          margin: "0 auto 12px",
          fontSize: "24px",
          transition: "all 0.3s ease",
        }}>
          {selectedFile ? "✅" : "📤"}
        </div>
        
        <h3 style={{
          fontSize: "14px",
          fontWeight: 600,
          color: selectedFile ? "#166534" : "#1a2332",
          margin: "0 0 4px 0",
        }}>
          Step 2: {selectedFile ? "File Selected" : "Upload CSV"}
        </h3>
        
        {selectedFile ? (
          <>
            <p style={{
              fontSize: "13px",
              fontWeight: 500,
              color: "#065f46",
              margin: "0 0 4px 0",
            }}>
              {selectedFile.name}
            </p>
            <p style={{
              fontSize: "11px",
              color: "#64748b",
              margin: "0 0 8px 0",
            }}>
              {(selectedFile.size / 1024).toFixed(1)} KB • Click to change
            </p>
            <div style={{
              display: "inline-flex",
              alignItems: "center",
              gap: "4px",
              padding: "4px 12px",
              borderRadius: "12px",
              background: "#f0fdf4",
              fontSize: "11px",
              color: "#166534",
              fontWeight: 500,
            }}>
              ✓ Ready to import
            </div>
          </>
        ) : (
          <>
            <p style={{
              fontSize: "13px",
              color: "#64748b",
              margin: "0 0 4px 0",
            }}>
              <span style={{ color: "#4a90d9", fontWeight: 500 }}>Click</span> or <span style={{ color: "#4a90d9", fontWeight: 500 }}>drag & drop</span>
            </p>
            <p style={{
              fontSize: "11px",
              color: "#94a3b8",
              margin: "0",
            }}>
              Supported: .CSV files only
            </p>
          </>
        )}
      </div>

      {/* Step 3: Import */}
      <div style={{
        background: "#ffffff",
        borderRadius: "12px",
        padding: "20px",
        border: "1px solid #eef2f6",
        textAlign: "center",
        transition: "all 0.3s ease",
        boxShadow: "0 2px 4px rgba(0, 0, 0, 0.02)",
        display: "flex",
        flexDirection: "column",
        justifyContent: "center",
      }}
      onMouseEnter={(e) => {
        if (selectedFile) {
          e.currentTarget.style.borderColor = "#d1d9e6";
          e.currentTarget.style.boxShadow = "0 8px 25px rgba(0, 0, 0, 0.06)";
        }
      }}
      onMouseLeave={(e) => {
        e.currentTarget.style.boxShadow = "0 2px 4px rgba(0, 0, 0, 0.02)";
        e.currentTarget.style.borderColor = "#eef2f6";
      }}
      >
        <div style={{
          width: "56px",
          height: "56px",
          borderRadius: "14px",
          background: selectedFile 
            ? "linear-gradient(135deg, #f0fdf4 0%, #dcfce7 100%)" 
            : "linear-gradient(135deg, #f1f5f9 0%, #e2e8f0 100%)",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          margin: "0 auto 12px",
          fontSize: "24px",
        }}>
          🚀
        </div>
        <h3 style={{
          fontSize: "14px",
          fontWeight: 600,
          color: selectedFile ? "#1a2332" : "#94a3b8",
          margin: "0 0 4px 0",
        }}>
          Step 3: Import Data
        </h3>
        <p style={{
          fontSize: "12px",
          color: selectedFile ? "#64748b" : "#94a3b8",
          margin: "0 0 16px 0",
          lineHeight: 1.5,
        }}>
          {selectedFile 
            ? "Import the uploaded CSV file" 
            : "Upload a file to enable import"}
        </p>
        <button
          onClick={handleImport}
          disabled={!selectedFile}
          style={{
            background: selectedFile 
              ? "linear-gradient(135deg, #10b981 0%, #059669 100%)"
              : "#e2e8f0",
            color: selectedFile ? "#fff" : "#94a3b8",
            border: "none",
            borderRadius: "8px",
            padding: "10px 20px",
            fontSize: "13px",
            fontWeight: 500,
            cursor: selectedFile ? "pointer" : "not-allowed",
            width: "100%",
            transition: "all 0.25s ease",
            boxShadow: selectedFile ? "0 2px 8px rgba(16, 185, 129, 0.25)" : "none",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            gap: "8px",
            opacity: selectedFile ? 1 : 0.5,
          }}
          onMouseEnter={(e) => {
            if (selectedFile) {
              e.target.style.transform = "translateY(-2px)";
              e.target.style.boxShadow = "0 4px 14px rgba(16, 185, 129, 0.35)";
              e.target.style.background = "linear-gradient(135deg, #059669 0%, #047857 100%)";
            }
          }}
          onMouseLeave={(e) => {
            if (selectedFile) {
              e.target.style.transform = "translateY(0)";
              e.target.style.boxShadow = "0 2px 8px rgba(16, 185, 129, 0.25)";
              e.target.style.background = "linear-gradient(135deg, #10b981 0%, #059669 100%)";
            }
          }}
        >
          <span>⚡</span> 
          {selectedFile ? "Import Now" : "No File"}
        </button>
      </div>
    </div>

    {/* Step Indicators */}
    <div style={{
      display: "flex",
      justifyContent: "center",
      gap: "8px",
      marginTop: "24px",
      position: "relative",
      zIndex: 1,
    }}>
      {[0, 1, 2].map((step) => {
        const isActive = step === 0 || (step === 1 && selectedFile) || (step === 2 && selectedFile);
        const isComplete = step === 0 || (step === 1 && selectedFile);
        return (
          <div
            key={step}
            style={{
              display: "flex",
              alignItems: "center",
              gap: "8px",
            }}
          >
            <div style={{
              width: "32px",
              height: "32px",
              borderRadius: "50%",
              background: isComplete 
                ? "linear-gradient(135deg, #10b981 0%, #059669 100%)"
                : isActive 
                  ? "linear-gradient(135deg, #4a90d9 0%, #357abd 100%)"
                  : "#e2e8f0",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              color: "#fff",
              fontSize: "12px",
              fontWeight: 600,
              transition: "all 0.3s ease",
            }}>
              {isComplete ? "✓" : step + 1}
            </div>
            {step < 2 && (
              <div style={{
                width: "40px",
                height: "2px",
                background: step === 0 && selectedFile ? "#10b981" : "#e2e8f0",
                transition: "all 0.3s ease",
              }} />
            )}
          </div>
        );
      })}
    </div>

    {/* Info Note */}
    <div style={{
      marginTop: "20px",
      padding: "14px 18px",
      background: "linear-gradient(135deg, #f8fafc 0%, #f1f5f9 100%)",
      borderRadius: "10px",
      border: "1px solid #eef2f6",
      position: "relative",
      zIndex: 1,
      display: "flex",
      alignItems: "flex-start",
      gap: "10px",
    }}>
      <span style={{
        fontSize: "16px",
        marginTop: "1px",
        flexShrink: 0,
      }}>💡</span>
      <div>
        <p style={{
          margin: 0,
          fontSize: "12px",
          color: "#475569",
          lineHeight: 1.6,
        }}>
          <strong style={{ color: "#1a2332" }}>Quick Tip:</strong> Make sure your CSV file follows the template format exactly. 
          Required fields: <span style={{
            background: "#e2e8f0",
            padding: "1px 6px",
            borderRadius: "4px",
            fontSize: "11px",
            color: "#1a2332",
            fontWeight: 500,
          }}>Month</span>, 
          <span style={{
            background: "#e2e8f0",
            padding: "1px 6px",
            borderRadius: "4px",
            fontSize: "11px",
            color: "#1a2332",
            fontWeight: 500,
            marginLeft: "4px",
          }}>Year</span>, 
          <span style={{
            background: "#e2e8f0",
            padding: "1px 6px",
            borderRadius: "4px",
            fontSize: "11px",
            color: "#1a2332",
            fontWeight: 500,
            marginLeft: "4px",
          }}>Salary Due</span>
        </p>
      </div>
    </div>

    {/* Add Pulse Animation */}
    <style>
      {`
        @keyframes pulse {
          0%, 100% { opacity: 1; transform: scale(1); }
          50% { opacity: 0.5; transform: scale(0.8); }
        }
      `}
    </style>
  </div>
)}
      </div>

      {/* View Salary Details Modal */}
      {viewModalOpen && viewingSalary && (
        <div style={{
          position: "fixed",
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          backgroundColor: "rgba(0,0,0,0.5)",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          zIndex: 1000,
          padding: "20px",
        }}>
          <div style={{
            backgroundColor: "white",
            borderRadius: "8px",
            padding: "24px",
            maxWidth: "600px",
            width: "100%",
            maxHeight: "90vh",
            overflowY: "auto",
            boxShadow: "0 4px 6px rgba(0,0,0,0.1)",
          }}>
            <div style={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
              marginBottom: "20px",
              borderBottom: "2px solid #e5e7eb",
              paddingBottom: "12px",
            }}>
              <h2 style={{ margin: 0, color: "#1f2937", fontSize: "20px", fontWeight: "600" }}>
                Salary Details
              </h2>
              <button
                onClick={() => setViewModalOpen(false)}
                style={{
                  background: "none",
                  border: "none",
                  fontSize: "24px",
                  cursor: "pointer",
                  color: "#6b7280",
                }}
              >
                ×
              </button>
            </div>

            <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: "16px" }}>
              <div>
                <p style={{ margin: "0 0 4px 0", fontSize: "12px", color: "#6b7280", fontWeight: "500" }}>Invoice Number</p>
                <p style={{ margin: 0, fontSize: "14px", color: "#1f2937", fontWeight: "600" }}>{viewingSalary.invoice_no || "N/A"}</p>
              </div>

              <div>
                <p style={{ margin: "0 0 4px 0", fontSize: "12px", color: "#6b7280", fontWeight: "500" }}>Month</p>
                <p style={{ margin: 0, fontSize: "14px", color: "#1f2937" }}>{viewingSalary.month || "N/A"}</p>
              </div>

              <div>
                <p style={{ margin: "0 0 4px 0", fontSize: "12px", color: "#6b7280", fontWeight: "500" }}>Year</p>
                <p style={{ margin: 0, fontSize: "14px", color: "#1f2937" }}>{viewingSalary.year || "N/A"}</p>
              </div>

              <div>
                <p style={{ margin: "0 0 4px 0", fontSize: "12px", color: "#6b7280", fontWeight: "500" }}>State</p>
                <p style={{ margin: 0, fontSize: "14px", color: "#1f2937" }}>{viewingSalary.state || "N/A"}</p>
              </div>

              <div>
                <p style={{ margin: "0 0 4px 0", fontSize: "12px", color: "#6b7280", fontWeight: "500" }}>District</p>
                <p style={{ margin: 0, fontSize: "14px", color: "#1f2937" }}>{viewingSalary.district || "N/A"}</p>
              </div>

              <div>
                <p style={{ margin: "0 0 4px 0", fontSize: "12px", color: "#6b7280", fontWeight: "500" }}>VC Name</p>
                <p style={{ margin: 0, fontSize: "14px", color: "#1f2937" }}>{viewingSalary.vc_name || "N/A"}</p>
              </div>

              <div>
                <p style={{ margin: "0 0 4px 0", fontSize: "12px", color: "#6b7280", fontWeight: "500" }}>Salary Due Date</p>
                <p style={{ margin: 0, fontSize: "14px", color: "#1f2937" }}>
                  {viewingSalary.salaryDueDate ? new Date(viewingSalary.salaryDueDate).toLocaleDateString() : "N/A"}
                </p>
              </div>

              <div>
                <p style={{ margin: "0 0 4px 0", fontSize: "12px", color: "#6b7280", fontWeight: "500" }}>Salary Paid Date</p>
                <p style={{ margin: 0, fontSize: "14px", color: "#1f2937" }}>
                  {viewingSalary.salaryPaidDate ? new Date(viewingSalary.salaryPaidDate).toLocaleDateString() : "N/A"}
                </p>
              </div>

              <div>
                <p style={{ margin: "0 0 4px 0", fontSize: "12px", color: "#6b7280", fontWeight: "500" }}>Salary Due (₹)</p>
                <p style={{ margin: 0, fontSize: "14px", color: "#1f2937", fontWeight: "600" }}>
                  ₹{(viewingSalary.salaryDue || 0).toLocaleString()}
                </p>
              </div>

              <div>
                <p style={{ margin: "0 0 4px 0", fontSize: "12px", color: "#6b7280", fontWeight: "500" }}>Salary Paid (₹)</p>
                <p style={{ margin: 0, fontSize: "14px", color: "#10b981", fontWeight: "600" }}>
                  ₹{(viewingSalary.salaryPaid || 0).toLocaleString()}
                </p>
              </div>

              <div>
                <p style={{ margin: "0 0 4px 0", fontSize: "12px", color: "#6b7280", fontWeight: "500" }}>Additional Due Amount (₹)</p>
                <p style={{ margin: 0, fontSize: "14px", color: "#1f2937" }}>
                  ₹{(viewingSalary.additionalDueAmount || 0).toLocaleString()}
                </p>
              </div>

              <div>
                <p style={{ margin: "0 0 4px 0", fontSize: "12px", color: "#6b7280", fontWeight: "500" }}>Additional Paid Amount (₹)</p>
                <p style={{ margin: 0, fontSize: "14px", color: "#1f2937" }}>
                  ₹{(viewingSalary.additionalPaidAmount || 0).toLocaleString()}
                </p>
              </div>

              <div>
                <p style={{ margin: "0 0 4px 0", fontSize: "12px", color: "#6b7280", fontWeight: "500" }}>Total Due Amount (₹)</p>
                <p style={{ margin: 0, fontSize: "14px", color: "#1f2937", fontWeight: "600" }}>
                  ₹{(viewingSalary.total_due_amount || 0).toLocaleString()}
                </p>
              </div>

              <div>
                <p style={{ margin: "0 0 4px 0", fontSize: "12px", color: "#6b7280", fontWeight: "500" }}>Total Paid Amount (₹)</p>
                <p style={{ margin: 0, fontSize: "14px", color: "#10b981", fontWeight: "600" }}>
                  ₹{(viewingSalary.total_paid_amount || 0).toLocaleString()}
                </p>
              </div>

              <div>
                <p style={{ margin: "0 0 4px 0", fontSize: "12px", color: "#6b7280", fontWeight: "500" }}>TDS Deducted (₹)</p>
                <p style={{ margin: 0, fontSize: "14px", color: "#ef4444" }}>
                  ₹{(viewingSalary.tds_deducted || 0).toLocaleString()}
                </p>
              </div>

              <div>
                <p style={{ margin: "0 0 4px 0", fontSize: "12px", color: "#6b7280", fontWeight: "500" }}>Adjustments (₹)</p>
                <p style={{ margin: 0, fontSize: "14px", color: "#1f2937" }}>
                  ₹{(viewingSalary.adjustments || 0).toLocaleString()}
                </p>
              </div>

              <div>
                <p style={{ margin: "0 0 4px 0", fontSize: "12px", color: "#6b7280", fontWeight: "500" }}>Unpaid Salary (₹)</p>
                <p style={{ margin: 0, fontSize: "14px", color: "#ef4444", fontWeight: "600" }}>
                  ₹{(viewingSalary.unpaid_salary || 0).toLocaleString()}
                </p>
              </div>

              <div>
                <p style={{ margin: "0 0 4px 0", fontSize: "12px", color: "#6b7280", fontWeight: "500" }}>Created By</p>
                <p style={{ margin: 0, fontSize: "14px", color: "#1f2937" }}>
                  {viewingSalary.created_by_details?.email || "N/A"}
                </p>
              </div>

              <div style={{ gridColumn: "1 / -1" }}>
                <p style={{ margin: "0 0 4px 0", fontSize: "12px", color: "#6b7280", fontWeight: "500" }}>Remarks</p>
                <p style={{ margin: 0, fontSize: "14px", color: "#1f2937" }}>
                  {viewingSalary.remarks || "N/A"}
                </p>
              </div>
            </div>

            <div style={{ marginTop: "24px", display: "flex", justifyContent: "flex-end" }}>
              <Button
                variant="contained"
                onClick={() => setViewModalOpen(false)}
                style={{
                  backgroundColor: "#6b7280",
                  color: "white",
                  padding: "8px 24px",
                }}
              >
                Close
              </Button>
            </div>
          </div>
        </div>
      )}

      {/* Add Salary Record Modal */}
      {isModalOpen && (
        <div style={{
          position: "fixed",
          top: 0,
          left: 0,
          width: "100%",
          height: "100%",
          backgroundColor: "rgba(0, 0, 0, 0.5)",
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
          zIndex: 9999,
          padding: "20px",
          boxSizing: "border-box",
        }}>
          <div style={{
            backgroundColor: "white",
            borderRadius: "12px",
            padding: "24px",
            maxWidth: "700px",
            width: "100%",
            maxHeight: "90vh",
            overflowY: "auto",
            boxShadow: "0 20px 25px -5px rgba(0, 0, 0, 0.1), 0 10px 10px -5px rgba(0, 0, 0, 0.04)",
          }}>
            {/* Header */}
            <div style={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
              marginBottom: "20px",
              borderBottom: "2px solid #e5e7eb",
              paddingBottom: "12px",
            }}>
              <div style={{ display: "flex", alignItems: "center", gap: "10px" }}>
                <span style={{ fontSize: "22px" }}>➕</span>
                <h2 style={{ margin: 0, color: "#1f2937", fontSize: "20px", fontWeight: "600" }}>
                  Add Salary Record
                </h2>
              </div>
              <button
                onClick={() => setIsModalOpen(false)}
                style={{
                  background: "none",
                  border: "none",
                  fontSize: "24px",
                  cursor: "pointer",
                  color: "#6b7280",
                }}
              >
                ×
              </button>
            </div>

            {/* Form */}
            <form onSubmit={handleSubmit}>
              <div style={{ display: "grid", gridTemplateColumns: isMobile ? "1fr" : "1fr 1fr", gap: "16px" }}>
                
                {/* Month */}
                <div>
                  <label style={{ display: "block", margin: "0 0 6px 0", fontSize: "13px", color: "#374151", fontWeight: "500" }}>
                    Month <span style={{ color: "#ef4444" }}>*</span>
                  </label>
                  <select
                    name="month"
                    value={formData.month || ""}
                    onChange={handleInputChange}
                    required
                    style={{
                      width: "100%",
                      padding: "8px 12px",
                      border: "1px solid #d1d5db",
                      borderRadius: "6px",
                      fontSize: "14px",
                      outline: "none",
                    }}
                  >
                    <option value="">Select Month</option>
                    <option value="January">January</option>
                    <option value="February">February</option>
                    <option value="March">March</option>
                    <option value="April">April</option>
                    <option value="May">May</option>
                    <option value="June">June</option>
                    <option value="July">July</option>
                    <option value="August">August</option>
                    <option value="September">September</option>
                    <option value="October">October</option>
                    <option value="November">November</option>
                    <option value="December">December</option>
                  </select>
                </div>

                {/* Year */}
                <div>
                  <label style={{ display: "block", margin: "0 0 6px 0", fontSize: "13px", color: "#374151", fontWeight: "500" }}>
                    Year <span style={{ color: "#ef4444" }}>*</span>
                  </label>
                  <select
                    name="year"
                    value={formData.year || ""}
                    onChange={handleInputChange}
                    required
                    style={{
                      width: "100%",
                      padding: "8px 12px",
                      border: "1px solid #d1d5db",
                      borderRadius: "6px",
                      fontSize: "14px",
                      outline: "none",
                    }}
                  >
                    <option value="">Select Year</option>
                    <option value="2023-2024">2023-2024</option>
                    <option value="2024-2025">2024-2025</option>
                    <option value="2025-2026">2025-2026</option>
                    <option value="2026-2027">2026-2027</option>
                    <option value="2027-2028">2027-2028</option>
                  </select>
                </div>

                {/* State */}
                <div>
                  <label style={{ display: "block", margin: "0 0 6px 0", fontSize: "13px", color: "#374151", fontWeight: "500" }}>
                    State <span style={{ color: "#ef4444" }}>*</span>
                  </label>
                  <select
                    name="state"
                    value={formData.state || ""}
                    onChange={handleInputChange}
                    required
                    style={{
                      width: "100%",
                      padding: "8px 12px",
                      border: "1px solid #d1d5db",
                      borderRadius: "6px",
                      fontSize: "14px",
                      outline: "none",
                    }}
                  >
                    <option value="">Select State</option>
                    {statelist && statelist.map((st) => (
                      <option key={st._id || st.state_name} value={st.state_name}>
                        {st.state_name}
                      </option>
                    ))}
                  </select>
                </div>

                {/* District */}
                <div>
                  <label style={{ display: "block", margin: "0 0 6px 0", fontSize: "13px", color: "#374151", fontWeight: "500" }}>
                    District
                  </label>
                  <select
                    name="district"
                    value={formData.district || ""}
                    onChange={handleInputChange}
                    disabled={!formData.state}
                    style={{
                      width: "100%",
                      padding: "8px 12px",
                      border: "1px solid #d1d5db",
                      borderRadius: "6px",
                      fontSize: "14px",
                      outline: "none",
                      backgroundColor: !formData.state ? "#f3f4f6" : "#ffffff",
                    }}
                  >
                    <option value="">Select District</option>
                    {districtOptions && districtOptions.map((d, i) => (
                      <option key={i} value={d}>
                        {d}
                      </option>
                    ))}
                  </select>
                </div>

                {/* VC Name */}
                <div style={{ gridColumn: isMobile ? "1" : "1 / -1" }}>
                  <label style={{ display: "block", margin: "0 0 6px 0", fontSize: "13px", color: "#374151", fontWeight: "500" }}>
                    VC Name <span style={{ color: "#ef4444" }}>*</span>
                  </label>
                  <select
                    name="vc"
                    value={formData.vc || ""}
                    onChange={handleInputChange}
                    required
                    style={{
                      width: "100%",
                      padding: "8px 12px",
                      border: "1px solid #d1d5db",
                      borderRadius: "6px",
                      fontSize: "14px",
                      outline: "none",
                    }}
                  >
                    <option value="">Select VC</option>
                    {vcOptions && vcOptions.length > 0
                      ? vcOptions.map((vc) => (
                          <option key={vc._id} value={vc.name}>
                            {vc.name} {vc.unique_id ? `(${vc.unique_id})` : ""}
                          </option>
                        ))
                      : vclist && vclist.filter(vc => vc.role === "vc").map((vc) => (
                          <option key={vc._id} value={vc.name}>
                            {vc.name} {vc.unique_id ? `(${vc.unique_id})` : ""}
                          </option>
                        ))
                    }
                  </select>
                </div>

                {/* Salary Due */}
                <div>
                  <label style={{ display: "block", margin: "0 0 6px 0", fontSize: "13px", color: "#374151", fontWeight: "500" }}>
                    Salary Due (₹) <span style={{ color: "#ef4444" }}>*</span>
                  </label>
                  <input
                    type="number"
                    name="salaryDue"
                    value={formData.salaryDue || ""}
                    onChange={handleInputChange}
                    placeholder="Enter salary due amount"
                    required
                    min="0"
                    style={{
                      width: "100%",
                      padding: "8px 12px",
                      border: "1px solid #d1d5db",
                      borderRadius: "6px",
                      fontSize: "14px",
                      outline: "none",
                    }}
                  />
                </div>

                {/* Additional Due Amount */}
                <div>
                  <label style={{ display: "block", margin: "0 0 6px 0", fontSize: "13px", color: "#374151", fontWeight: "500" }}>
                    Additional Due Amount (₹)
                  </label>
                  <input
                    type="number"
                    name="additionalDueAmount"
                    value={formData.additionalDueAmount || ""}
                    onChange={handleInputChange}
                    placeholder="Enter additional due amount"
                    min="0"
                    style={{
                      width: "100%",
                      padding: "8px 12px",
                      border: "1px solid #d1d5db",
                      borderRadius: "6px",
                      fontSize: "14px",
                      outline: "none",
                    }}
                  />
                </div>

                {/* Total Due Amount Preview */}
                <div style={{ gridColumn: isMobile ? "1" : "1 / -1" }}>
                  <div style={{
                    background: "#f0fdf4",
                    border: "1px solid #bbf7d0",
                    borderRadius: "8px",
                    padding: "12px 16px",
                    display: "flex",
                    justifyContent: "space-between",
                    alignItems: "center",
                  }}>
                    <span style={{ fontSize: "13px", color: "#166534", fontWeight: "500" }}>
                      💰 Total Due Amount (Auto-Calculated):
                    </span>
                    <span style={{ fontSize: "16px", color: "#166534", fontWeight: "700" }}>
                      ₹{((Number(formData.salaryDue) || 0) + (Number(formData.additionalDueAmount) || 0)).toLocaleString()}
                    </span>
                  </div>
                </div>

              </div>

              {/* Action Buttons */}
              <div style={{ marginTop: "24px", display: "flex", justifyContent: "flex-end", gap: "12px" }}>
                <button
                  type="button"
                  onClick={() => setIsModalOpen(false)}
                  style={{
                    backgroundColor: "#f3f4f6",
                    color: "#374151",
                    border: "1px solid #d1d5db",
                    borderRadius: "6px",
                    padding: "9px 20px",
                    fontSize: "14px",
                    fontWeight: "500",
                    cursor: "pointer",
                  }}
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  style={{
                    background: "linear-gradient(135deg, #10b981 0%, #059669 100%)",
                    color: "white",
                    border: "none",
                    borderRadius: "6px",
                    padding: "9px 24px",
                    fontSize: "14px",
                    fontWeight: "600",
                    cursor: "pointer",
                    boxShadow: "0 2px 6px rgba(16, 185, 129, 0.3)",
                  }}
                >
                  Create Record
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Edit Salary Modal - Superadmin: all fields, HR: only 7 fields */}
      {(() => {
        console.log('🎨 EDIT MODAL RENDER:', { editModalOpen, hasEditingSalary: !!editingSalary, userRole: user?.role });
        return null;
      })()}
      {editModalOpen && editingSalary && (
        <div style={{
          position: "fixed",
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          backgroundColor: "rgba(0,0,0,0.5)",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          zIndex: 1000,
          padding: "20px",
        }}>
          <div style={{
            backgroundColor: "white",
            borderRadius: "8px",
            padding: "24px",
            maxWidth: "700px",
            width: "100%",
            maxHeight: "90vh",
            overflowY: "auto",
            boxShadow: "0 4px 6px rgba(0,0,0,0.1)",
          }}>
            <div style={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
              marginBottom: "20px",
              borderBottom: "2px solid #e5e7eb",
              paddingBottom: "12px",
            }}>
              <h2 style={{ margin: 0, color: "#1f2937", fontSize: "20px", fontWeight: "600" }}>
                Edit Salary {user?.role === "superadmin" && " (Full Access)"}
              </h2>
              <button
                onClick={() => {
                  setEditModalOpen(false);
                  setEditingSalary(null);
                }}
                style={{
                  background: "none",
                  border: "none",
                  fontSize: "24px",
                  cursor: "pointer",
                  color: "#6b7280",
                }}
              >
                ×
              </button>
            </div>

            <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: "16px" }}>
              
              {/* Invoice Number - Always editable */}
              <div>
                <label style={{ display: "block", margin: "0 0 4px 0", fontSize: "12px", color: "#6b7280", fontWeight: "500" }}>
                  Invoice Number
                </label>
                <input
                  type="text"
                  value={editingSalary.invoice_no || ""}
                  onChange={(e) => setEditingSalary({...editingSalary, invoice_no: e.target.value})}
                  style={{
                    width: "100%",
                    padding: "8px",
                    border: "1px solid #d1d5db",
                    borderRadius: "4px",
                    fontSize: "14px",
                  }}
                />
              </div>

              {/* Month - Dropdown for superadmin, read-only for others */}
              <div>
                <label style={{ display: "block", margin: "0 0 4px 0", fontSize: "12px", color: "#6b7280", fontWeight: "500" }}>
                  Month {user?.role !== "superadmin" && "(Read-only)"}
                </label>
                {user?.role === "superadmin" ? (
                  <select
                    value={editingSalary.month || ""}
                    onChange={(e) => setEditingSalary({...editingSalary, month: e.target.value})}
                    style={{
                      width: "100%",
                      padding: "8px",
                      border: "1px solid #d1d5db",
                      borderRadius: "4px",
                      fontSize: "14px",
                    }}
                  >
                    <option value="">Select Month</option>
                    <option value="January">January</option>
                    <option value="February">February</option>
                    <option value="March">March</option>
                    <option value="April">April</option>
                    <option value="May">May</option>
                    <option value="June">June</option>
                    <option value="July">July</option>
                    <option value="August">August</option>
                    <option value="September">September</option>
                    <option value="October">October</option>
                    <option value="November">November</option>
                    <option value="December">December</option>
                  </select>
                ) : (
                  <input
                    type="text"
                    value={editingSalary.month || ""}
                    disabled
                    style={{
                      width: "100%",
                      padding: "8px",
                      border: "1px solid #e5e7eb",
                      borderRadius: "4px",
                      fontSize: "14px",
                      backgroundColor: "#f9fafb",
                      color: "#6b7280",
                    }}
                  />
                )}
              </div>

              {/* Year - Dropdown for superadmin, read-only for others */}
              <div>
                <label style={{ display: "block", margin: "0 0 4px 0", fontSize: "12px", color: "#6b7280", fontWeight: "500" }}>
                  Year {user?.role !== "superadmin" && "(Read-only)"}
                </label>
                {user?.role === "superadmin" ? (
                  <select
                    value={editingSalary.year || ""}
                    onChange={(e) => setEditingSalary({...editingSalary, year: e.target.value})}
                    style={{
                      width: "100%",
                      padding: "8px",
                      border: "1px solid #d1d5db",
                      borderRadius: "4px",
                      fontSize: "14px",
                    }}
                  >
                    <option value="">Select Year</option>
                    {Array.from({length: 10}, (_, i) => new Date().getFullYear() - 5 + i).map(year => (
                      <option key={year} value={year}>{year}</option>
                    ))}
                  </select>
                ) : (
                  <input
                    type="text"
                    value={editingSalary.year || ""}
                    disabled
                    style={{
                      width: "100%",
                      padding: "8px",
                      border: "1px solid #e5e7eb",
                      borderRadius: "4px",
                      fontSize: "14px",
                      backgroundColor: "#f9fafb",
                      color: "#6b7280",
                    }}
                  />
                )}
              </div>

              {/* State - Dropdown for superadmin, read-only for others */}
              <div>
                <label style={{ display: "block", margin: "0 0 4px 0", fontSize: "12px", color: "#6b7280", fontWeight: "500" }}>
                  State {user?.role !== "superadmin" && "(Read-only)"}
                </label>
                {user?.role === "superadmin" ? (
                  <select
                    value={editingSalary.state_name || editingSalary.state || ""}
                    onChange={(e) => setEditingSalary({...editingSalary, state_name: e.target.value, state: e.target.value})}
                    style={{
                      width: "100%",
                      padding: "8px",
                      border: "1px solid #d1d5db",
                      borderRadius: "4px",
                      fontSize: "14px",
                    }}
                  >
                    <option value="">Select State</option>
                    {statelist && statelist.map((state) => (
                      <option key={state._id} value={state.state_name}>
                        {state.state_name}
                      </option>
                    ))}
                  </select>
                ) : (
                  <input
                    type="text"
                    value={editingSalary.state_name || editingSalary.state || ""}
                    disabled
                    style={{
                      width: "100%",
                      padding: "8px",
                      border: "1px solid #e5e7eb",
                      borderRadius: "4px",
                      fontSize: "14px",
                      backgroundColor: "#f9fafb",
                      color: "#6b7280",
                    }}
                  />
                )}
              </div>

              {/* District - Editable only for superadmin */}
              <div>
                <label style={{ display: "block", margin: "0 0 4px 0", fontSize: "12px", color: "#6b7280", fontWeight: "500" }}>
                  District {user?.role !== "superadmin" && "(Read-only)"}
                </label>
                <input
                  type="text"
                  value={editingSalary.district || ""}
                  onChange={(e) => setEditingSalary({...editingSalary, district: e.target.value})}
                  disabled={user?.role !== "superadmin"}
                  style={{
                    width: "100%",
                    padding: "8px",
                    border: user?.role === "superadmin" ? "1px solid #d1d5db" : "1px solid #e5e7eb",
                    borderRadius: "4px",
                    fontSize: "14px",
                    backgroundColor: user?.role === "superadmin" ? "white" : "#f9fafb",
                    color: user?.role === "superadmin" ? "#1f2937" : "#6b7280",
                  }}
                />
              </div>

              {/* VC Name - Dropdown for superadmin, read-only for others */}
              <div>
                <label style={{ display: "block", margin: "0 0 4px 0", fontSize: "12px", color: "#6b7280", fontWeight: "500" }}>
                  VC Name {user?.role !== "superadmin" && "(Read-only)"}
                </label>
                {user?.role === "superadmin" ? (
                  <select
                    value={editingSalary.vc_name || ""}
                    onChange={(e) => setEditingSalary({...editingSalary, vc_name: e.target.value})}
                    style={{
                      width: "100%",
                      padding: "8px",
                      border: "1px solid #d1d5db",
                      borderRadius: "4px",
                      fontSize: "14px",
                    }}
                  >
                    <option value="">Select VC</option>
                    {vclist && vclist.filter(vc => vc.role === "vc").map((vc) => (
                      <option key={vc._id} value={vc.name}>
                        {vc.name}
                      </option>
                    ))}
                  </select>
                ) : (
                  <input
                    type="text"
                    value={editingSalary.vc_name || ""}
                    disabled
                    style={{
                      width: "100%",
                      padding: "8px",
                      border: "1px solid #e5e7eb",
                      borderRadius: "4px",
                      fontSize: "14px",
                      backgroundColor: "#f9fafb",
                      color: "#6b7280",
                    }}
                  />
                )}
              </div>

              {/* Salary Due Date - Calculated field, read-only */}
              <div>
                <label style={{ display: "block", margin: "0 0 4px 0", fontSize: "12px", color: "#6b7280", fontWeight: "500" }}>
                  Salary Due Date (Auto-calculated)
                </label>
                <input
                  type="text"
                  value={(() => {
                    const monthNames = ["January", "February", "March", "April", "May", "June", "July", "August", "September", "October", "November", "December"];
                    const monthIndex = monthNames.indexOf(editingSalary.month);
                    if (monthIndex !== -1 && editingSalary.year) {
                      const lastDate = new Date(parseInt(editingSalary.year), monthIndex + 1, 0);
                      return lastDate.toISOString().split('T')[0];
                    }
                    return "N/A";
                  })()}
                  disabled
                  style={{
                    width: "100%",
                    padding: "8px",
                    border: "1px solid #e5e7eb",
                    borderRadius: "4px",
                    fontSize: "14px",
                    backgroundColor: "#f9fafb",
                    color: "#6b7280",
                  }}
                />
              </div>

              {/* Salary Paid Date - Always editable */}
              <div>
                <label style={{ display: "block", margin: "0 0 4px 0", fontSize: "12px", color: "#6b7280", fontWeight: "500" }}>
                  Salary Paid Date
                </label>
                <input
                  type="date"
                  value={editingSalary.salaryPaidDate ? editingSalary.salaryPaidDate.split('T')[0] : ""}
                  onChange={(e) => setEditingSalary({...editingSalary, salaryPaidDate: e.target.value})}
                  style={{
                    width: "100%",
                    padding: "8px",
                    border: "1px solid #d1d5db",
                    borderRadius: "4px",
                    fontSize: "14px",
                  }}
                />
              </div>

              {/* Salary Due - Editable only for superadmin */}
              <div>
                <label style={{ display: "block", margin: "0 0 4px 0", fontSize: "12px", color: "#6b7280", fontWeight: "500" }}>
                  Salary Due (₹) {user?.role !== "superadmin" && "(Read-only)"}
                </label>
                <input
                  type="number"
                  value={editingSalary.salaryDue || 0}
                  onChange={(e) => setEditingSalary({...editingSalary, salaryDue: parseFloat(e.target.value) || 0})}
                  disabled={user?.role !== "superadmin"}
                  style={{
                    width: "100%",
                    padding: "8px",
                    border: user?.role === "superadmin" ? "1px solid #d1d5db" : "1px solid #e5e7eb",
                    borderRadius: "4px",
                    fontSize: "14px",
                    backgroundColor: user?.role === "superadmin" ? "white" : "#f9fafb",
                    color: user?.role === "superadmin" ? "#1f2937" : "#6b7280",
                  }}
                />
              </div>

              {/* Additional Due Amount - Editable only for superadmin */}
              <div>
                <label style={{ display: "block", margin: "0 0 4px 0", fontSize: "12px", color: "#6b7280", fontWeight: "500" }}>
                  Additional Due Amount (₹) {user?.role !== "superadmin" && "(Read-only)"}
                </label>
                <input
                  type="number"
                  value={editingSalary.additionalDueAmount || 0}
                  onChange={(e) => setEditingSalary({...editingSalary, additionalDueAmount: parseFloat(e.target.value) || 0})}
                  disabled={user?.role !== "superadmin"}
                  style={{
                    width: "100%",
                    padding: "8px",
                    border: user?.role === "superadmin" ? "1px solid #d1d5db" : "1px solid #e5e7eb",
                    borderRadius: "4px",
                    fontSize: "14px",
                    backgroundColor: user?.role === "superadmin" ? "white" : "#f9fafb",
                    color: user?.role === "superadmin" ? "#1f2937" : "#6b7280",
                  }}
                />
              </div>

              {/* Total Due Amount - Calculated field, read-only */}
              <div>
                <label style={{ display: "block", margin: "0 0 4px 0", fontSize: "12px", color: "#6b7280", fontWeight: "500" }}>
                  Total Due Amount (₹) (Auto-calculated)
                </label>
                <input
                  type="text"
                  value={(parseFloat(editingSalary.salaryDue || 0) + parseFloat(editingSalary.additionalDueAmount || 0)).toFixed(2)}
                  disabled
                  style={{
                    width: "100%",
                    padding: "8px",
                    border: "1px solid #e5e7eb",
                    borderRadius: "4px",
                    fontSize: "14px",
                    backgroundColor: "#f9fafb",
                    color: "#6b7280",
                  }}
                />
              </div>

              {/* Salary Paid - Always editable */}
              <div>
                <label style={{ display: "block", margin: "0 0 4px 0", fontSize: "12px", color: "#6b7280", fontWeight: "500" }}>
                  Salary Paid (₹)
                </label>
                <input
                  type="number"
                  value={editingSalary.salaryPaid || 0}
                  onChange={(e) => setEditingSalary({...editingSalary, salaryPaid: parseFloat(e.target.value) || 0})}
                  style={{
                    width: "100%",
                    padding: "8px",
                    border: "1px solid #d1d5db",
                    borderRadius: "4px",
                    fontSize: "14px",
                  }}
                />
              </div>

              {/* Additional Paid Amount - Always editable */}
              <div>
                <label style={{ display: "block", margin: "0 0 4px 0", fontSize: "12px", color: "#6b7280", fontWeight: "500" }}>
                  Additional Paid Amount (₹)
                </label>
                <input
                  type="number"
                  value={editingSalary.additionalPaidAmount || 0}
                  onChange={(e) => setEditingSalary({...editingSalary, additionalPaidAmount: parseFloat(e.target.value) || 0})}
                  style={{
                    width: "100%",
                    padding: "8px",
                    border: "1px solid #d1d5db",
                    borderRadius: "4px",
                    fontSize: "14px",
                  }}
                />
              </div>

              {/* Total Paid Amount - Calculated field, read-only */}
              <div>
                <label style={{ display: "block", margin: "0 0 4px 0", fontSize: "12px", color: "#6b7280", fontWeight: "500" }}>
                  Total Paid Amount (₹) (Auto-calculated)
                </label>
                <input
                  type="text"
                  value={(parseFloat(editingSalary.salaryPaid || 0) + parseFloat(editingSalary.additionalPaidAmount || 0)).toFixed(2)}
                  disabled
                  style={{
                    width: "100%",
                    padding: "8px",
                    border: "1px solid #e5e7eb",
                    borderRadius: "4px",
                    fontSize: "14px",
                    backgroundColor: "#f9fafb",
                    color: "#6b7280",
                  }}
                />
              </div>

              {/* TDS Deducted (Deductions) - Always editable */}
              <div>
                <label style={{ display: "block", margin: "0 0 4px 0", fontSize: "12px", color: "#6b7280", fontWeight: "500" }}>
                  Deductions (₹)
                </label>
                <input
                  type="number"
                  value={editingSalary.tds_deducted || 0}
                  onChange={(e) => setEditingSalary({...editingSalary, tds_deducted: parseFloat(e.target.value) || 0})}
                  style={{
                    width: "100%",
                    padding: "8px",
                    border: "1px solid #d1d5db",
                    borderRadius: "4px",
                    fontSize: "14px",
                  }}
                />
              </div>

              {/* Adjustments - Always editable */}
              <div>
                <label style={{ display: "block", margin: "0 0 4px 0", fontSize: "12px", color: "#6b7280", fontWeight: "500" }}>
                  Adjustments (₹)
                </label>
                <input
                  type="number"
                  value={editingSalary.adjustments || 0}
                  onChange={(e) => setEditingSalary({...editingSalary, adjustments: parseFloat(e.target.value) || 0})}
                  style={{
                    width: "100%",
                    padding: "8px",
                    border: "1px solid #d1d5db",
                    borderRadius: "4px",
                    fontSize: "14px",
                  }}
                />
              </div>

              {/* Total Salary Paid - Calculated field, read-only */}
              <div>
                <label style={{ display: "block", margin: "0 0 4px 0", fontSize: "12px", color: "#6b7280", fontWeight: "500" }}>
                  Total Salary Paid (₹) (Auto-calculated)
                </label>
                <input
                  type="text"
                  value={(parseFloat(editingSalary.salaryPaid || 0) + parseFloat(editingSalary.additionalPaidAmount || 0) + parseFloat(editingSalary.tds_deducted || 0) + parseFloat(editingSalary.adjustments || 0)).toFixed(2)}
                  disabled
                  style={{
                    width: "100%",
                    padding: "8px",
                    border: "1px solid #e5e7eb",
                    borderRadius: "4px",
                    fontSize: "14px",
                    backgroundColor: "#f9fafb",
                    color: "#6b7280",
                  }}
                />
              </div>

              {/* Unpaid Salary - Calculated field, read-only */}
              <div>
                <label style={{ display: "block", margin: "0 0 4px 0", fontSize: "12px", color: "#6b7280", fontWeight: "500" }}>
                  Unpaid Salary (₹) (Auto-calculated)
                </label>
                <input
                  type="text"
                  value={(() => {
                    const totalDue = parseFloat(editingSalary.salaryDue || 0) + parseFloat(editingSalary.additionalDueAmount || 0);
                    const totalSalaryPaid = parseFloat(editingSalary.salaryPaid || 0) + parseFloat(editingSalary.additionalPaidAmount || 0) + parseFloat(editingSalary.tds_deducted || 0) + parseFloat(editingSalary.adjustments || 0);
                    return (totalDue - totalSalaryPaid).toFixed(2);
                  })()}
                  disabled
                  style={{
                    width: "100%",
                    padding: "8px",
                    border: "1px solid #e5e7eb",
                    borderRadius: "4px",
                    fontSize: "14px",
                    backgroundColor: "#f9fafb",
                    color: "#6b7280",
                  }}
                />
              </div>

              {/* Created by - Read-only */}
              <div>
                <label style={{ display: "block", margin: "0 0 4px 0", fontSize: "12px", color: "#6b7280", fontWeight: "500" }}>
                  Created by (Read-only)
                </label>
                <input
                  type="text"
                  value={editingSalary.created_by_details?.email || "N/A"}
                  disabled
                  style={{
                    width: "100%",
                    padding: "8px",
                    border: "1px solid #e5e7eb",
                    borderRadius: "4px",
                    fontSize: "14px",
                    backgroundColor: "#f9fafb",
                    color: "#6b7280",
                  }}
                />
              </div>

              {/* Remarks - Always editable */}
              <div style={{ gridColumn: "1 / -1" }}>
                <label style={{ display: "block", margin: "0 0 4px 0", fontSize: "12px", color: "#6b7280", fontWeight: "500" }}>
                  Remarks
                </label>
                <textarea
                  value={editingSalary.remarks || ""}
                  onChange={(e) => setEditingSalary({...editingSalary, remarks: e.target.value})}
                  rows={3}
                  style={{
                    width: "100%",
                    padding: "8px",
                    border: "1px solid #d1d5db",
                    borderRadius: "4px",
                    fontSize: "14px",
                    resize: "vertical",
                  }}
                />
              </div>
            </div>

            <div style={{ marginTop: "24px", display: "flex", justifyContent: "flex-end", gap: "12px" }}>
              <Button
                variant="contained"
                onClick={() => {
                  setEditModalOpen(false);
                  setEditingSalary(null);
                }}
                style={{
                  backgroundColor: "#6b7280",
                  color: "white",
                  padding: "8px 24px",
                }}
              >
                Cancel
              </Button>
              <Button
                variant="contained"
                onClick={handleEditSubmit}
                style={{
                  backgroundColor: "#3b82f6",
                  color: "white",
                  padding: "8px 24px",
                }}
              >
                Save Changes
              </Button>
            </div>
          </div>
        </div>
      )}

      {/* Bulk Delete Confirmation Modal */}
      {bulkDeleteModal.open && (
        <div style={{
          position: "fixed",
          top: 0,
          left: 0,
          width: "100%",
          height: "100%",
          backgroundColor: "rgba(0, 0, 0, 0.5)",
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
          zIndex: 10000,
        }}>
          <div style={{
            backgroundColor: "white",
            borderRadius: "8px",
            padding: "24px",
            maxWidth: "500px",
            width: "90%",
            boxShadow: "0 4px 6px rgba(0, 0, 0, 0.1)",
          }}>
            <h3 style={{
              fontSize: "20px",
              fontWeight: "600",
              marginBottom: "16px",
              color: "#1a202c",
            }}>
              Delete Selected Salary Records?
            </h3>
            <p style={{
              fontSize: "14px",
              color: "#4a5568",
              marginBottom: "20px",
              lineHeight: "1.5",
            }}>
              This action will permanently delete {selectedSalaryIds.length} selected salary record(s). This cannot be undone.
            </p>

            <div style={{ display: "flex", justifyContent: "flex-end", gap: "12px" }}>
              <button
                onClick={() => setBulkDeleteModal({ open: false, type: '' })}
                style={{
                  backgroundColor: "#e2e8f0",
                  color: "#2d3748",
                  border: "none",
                  borderRadius: "6px",
                  padding: "8px 16px",
                  fontSize: "14px",
                  fontWeight: "500",
                  cursor: "pointer",
                }}
              >
                Cancel
              </button>
              <button
                onClick={confirmBulkDelete}
                style={{
                  backgroundColor: "#e53e3e",
                  color: "white",
                  border: "none",
                  borderRadius: "6px",
                  padding: "8px 16px",
                  fontSize: "14px",
                  fontWeight: "500",
                  cursor: "pointer",
                }}
              >
                Delete Selected
              </button>
            </div>
          </div>
        </div>
      )}

      {/* CSV Pre-Validation Error Modal */}
      <CsvValidationModal
        open={!!csvValidationErrors}
        onClose={() => setCsvValidationErrors(null)}
        errorData={csvValidationErrors}
      />
    </div>
  );
};

export default SalaryPayment;
